import './App.css';
import organicTag from './assets/organic.png';
import trappedIcon from './assets/trapped.png';
import luredIcon from './assets/lured.png';
import { useEffect, useRef, useState } from 'react';
import {
  COG_ACCURACY_OPTIONS,
  COG_OPTIONS,
  COG_TARGET_OPTIONS,
  COG_USAGE_OPTIONS,
} from './data/cogs';
import { DAMAGE_GAG_TRACKS, GAG_TRACKS } from './data/gags';
import {
  getCogAttacks,
  getCogDefense,
  getCogDepartment,
  getCogImageSrc,
  getCogLabel,
  getLevelOptions,
  getMaxHp,
} from './utils/cogs';
import {
  getGagAccuracy,
  getGagImageSrc,
  getGagInfo,
  getGagPower,
  getGagPowerRange,
} from './utils/gags';
import { humanizeCamelCase } from './utils/format';

const rollChance = (percent) => Math.random() * 100 < percent;

const getRandomIntInclusive = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const BATTLE_COG_VALUES = {
  cashbot: COG_OPTIONS.filter(
    (option) => getCogDepartment(option.value) === 'cashbot'
  ).map((option) => option.value),
  lawbot: COG_OPTIONS.filter(
    (option) => getCogDepartment(option.value) === 'lawbot'
  ).map((option) => option.value),
  bossbot: COG_OPTIONS.filter(
    (option) => getCogDepartment(option.value) === 'bossbot'
  ).map((option) => option.value),
};

const BATTLE_CONFIG = {
  cfo: {
    department: 'cashbot',
    levelMin: 1,
    levelMax: 12,
    poolMin: 30,
    poolMax: 40,
    skelecogChance: 0.5,
    skelecogMinLevel: 8,
  },
  cj: {
    department: 'lawbot',
    levelMin: 8,
    levelMax: 13,
    poolMin: 40,
    poolMax: 45,
    skelecogChance: 0,
    skelecogMinLevel: 0,
  },
  ceo: {
    department: 'bossbot',
    levelMin: 9,
    levelMax: 14,
    poolMin: 30,
    poolMax: 40,
    skelecogChance: 0,
    skelecogMinLevel: 0,
    v2Chance: 0.5,
  },
};

const getBattleConfig = (type) => BATTLE_CONFIG[type] ?? BATTLE_CONFIG.cfo;

const GAG_TRACK_COLORS = {
  'Toon-Up': '#b567e0',
  Trap: '#e5e472',
  Lure: '#68b84c',
  Sound: '#4f62e2',
  Throw: '#dca24b',
  Squirt: '#dd6bd0',
  Drop: '#83e6eb',
};

const hexToRgb = (hex) => {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return [0, 0, 0];
  const value = Number.parseInt(normalized, 16);
  if (Number.isNaN(value)) return [0, 0, 0];
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

const getSeededIndex = (seed, key, length) => {
  if (length <= 0) return 0;
  const str = `${seed}:${key}`;
  let hash = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash) % length;
};

const getTotalDamageMulti = (damage, toons) =>
  toons.reduce(
    (sum, toon) => sum + Math.min(damage, toon.currentLaff ?? 0),
    0
  );

const getMinSingleDamage = (damage, toons) =>
  Math.min(...toons.map((toon) => Math.min(damage, toon.currentLaff ?? 0)));

const getMaxSingleDamage = (damage, toons) =>
  Math.max(...toons.map((toon) => Math.min(damage, toon.currentLaff ?? 0)));

const pickCogAttack = (attacks, toons, usage, randomSeed, randomKey) => {
  const attackEntries = Object.entries(attacks ?? {});
  if (attackEntries.length === 0) return null;
  if (!toons.length) return attackEntries[0][0];
  if (usage === 'random') {
    const randomIndex =
      randomSeed == null || randomKey == null
        ? Math.floor(Math.random() * attackEntries.length)
        : getSeededIndex(randomSeed, randomKey, attackEntries.length);
    return attackEntries[randomIndex][0];
  }

  const scored = attackEntries.map(([name, attack]) => {
    const damage = attack.damage ?? 0;
    const affects = attack.affects ?? 'single';
    let score = 0;
    if (usage === 'weakest') {
      score =
        affects === 'multi'
          ? getTotalDamageMulti(damage, toons)
          : getMinSingleDamage(damage, toons);
      return { name, score };
    }
    if (usage === 'strongest') {
      score =
        affects === 'multi'
          ? getTotalDamageMulti(damage, toons)
          : getMaxSingleDamage(damage, toons);
      return { name, score };
    }
    score = getMaxSingleDamage(damage, toons);
    return { name, score };
  });

  const targetScore =
    usage === 'weakest'
      ? Math.min(...scored.map((item) => item.score))
      : Math.max(...scored.map((item) => item.score));
  const candidates = scored.filter((item) => item.score === targetScore);
  return candidates[Math.floor(Math.random() * candidates.length)].name;
};

const pickCogTarget = (toons, config, randomSeed, randomKey) => {
  if (!toons.length) return null;
  if (config === 'random') {
    const index = getSeededIndex(randomSeed ?? 0, randomKey ?? 'target', toons.length);
    return toons[index]?.id ?? null;
  }
  const scores = toons.map((toon) => toon.currentLaff ?? 0);
  const targetValue =
    config === 'weakest' ? Math.min(...scores) : Math.max(...scores);
  const candidates = toons.filter(
    (toon) => (toon.currentLaff ?? 0) === targetValue
  );
  if (candidates.length === 1) return candidates[0].id;
  const index = getSeededIndex(randomSeed ?? 0, randomKey ?? 'target', candidates.length);
  return candidates[index]?.id ?? null;
};

const getAttackPreview = (attack, toons) => {
  if (!attack) return { total: 0, breakdown: null };
  const damage = attack.damage ?? 0;
  if (attack.affects === 'multi') {
    const perToon = toons.map((toon) =>
      Math.min(damage, toon.currentLaff ?? 0)
    );
    const total = perToon.reduce((sum, value) => sum + value, 0);
    const uniqueValues = new Set(perToon);
    const breakdown =
      uniqueValues.size <= 1
        ? `${perToon.length}*${perToon[0] ?? 0}`
        : perToon.join('+');
    return { total, breakdown };
  }
  return { total: getMaxSingleDamage(damage, toons), breakdown: null };
};

const cloneCog = (cog) => ({ ...cog });

const cloneToon = (toon) => ({
  ...toon,
  gag: toon.gag ? { ...toon.gag } : null,
  target: toon.target ? { ...toon.target } : null,
  gagSetup: toon.gagSetup
    ? toon.gagSetup.map((entry) => ({ ...entry }))
    : toon.gagSetup,
  organicGags: toon.organicGags ? [...toon.organicGags] : toon.organicGags,
  passSelected: toon.passSelected ?? false,
  turnResolved: toon.turnResolved ?? false,
});

const SHOW_ATTACK_PREVIEW =
  process.env.REACT_APP_SHOW_ATTACK_PREVIEW === 'TRUE';
const SHOW_MANUAL_GAG_ACCURACY_SLIDER =
  process.env.REACT_APP_SHOW_MANUAL_GAG_ACCURACY_SLIDER === 'TRUE';
const SHOW_SQUAD_SETUP_BUTTON =
  process.env.REACT_APP_SQUAD_SETUP_BUTTON === 'TRUE';
const SHOW_TOON_DEFEAT_TIPS =
  process.env.REACT_APP_SHOW_TOON_DEFEAT_TIPS === 'TRUE';
const ACCURACY_CAP = (() => {
  const raw = Number.parseFloat(process.env.REACT_APP_ACCURACY_CAP);
  if (Number.isFinite(raw)) {
    return Math.max(raw, 0);
  }
  return 100;
})();

const clampAccuracyValue = (value) =>
  Math.min(Math.max(value ?? 0, 0), ACCURACY_CAP);

const getAutoTarget = (toon, allToons, allCogs) => {
  if (!toon?.gag) return null;
  const gagInfo = getGagInfo(toon.gag);
  if (!gagInfo) return null;
  if (gagInfo.trackName === 'Trap') {
    if (gagInfo.targeting === 'all-cogs') {
      const anyTrapped = allCogs.some((cog) => cog.status === 'trapped');
      const allLured =
        allCogs.length > 0 && allCogs.every((cog) => cog.status === 'lured');
      return !anyTrapped && !allLured && allCogs.length
        ? { type: 'all-cogs' }
        : null;
    }
    const validCogs = allCogs.filter((cog) => !cog.status);
    if (validCogs.length === 1) {
      return { type: 'cog', id: validCogs[0].id };
    }
    return null;
  }
  if (gagInfo.trackName === 'Lure') {
    const validCogs = allCogs.filter((cog) => cog.status !== 'lured');
    if (gagInfo.targeting === 'all-cogs') {
      return validCogs.length ? { type: 'all-cogs' } : null;
    }
    if (validCogs.length === 1) {
      return { type: 'cog', id: validCogs[0].id };
    }
    return null;
  }
  if (gagInfo.targeting === 'all-cogs') {
    return allCogs.length ? { type: 'all-cogs' } : null;
  }
  if (gagInfo.trackName === 'Toon-Up') {
    if (allToons.length <= 1) return null;
    if (gagInfo.targeting === 'all-toons-except-self') {
      return { type: 'all-toons-except-self' };
    }
    if (allToons.length !== 2) return null;
    const otherToon = allToons.find((candidate) => candidate.id !== toon.id);
    return otherToon ? { type: 'toon', id: otherToon.id } : null;
  }
  if (allCogs.length === 1) {
    return { type: 'cog', id: allCogs[0].id };
  }
  return null;
};

const buildDefaultCog = (id, value = COG_OPTIONS[0]?.value ?? '') => {
  const defaultLevel = String(getLevelOptions(value)[0] ?? '');
  const maxHp = getMaxHp(defaultLevel);
  return {
    id,
    value,
    level: defaultLevel,
    currentHp: maxHp,
    defense: getCogDefense(defaultLevel, value),
    skelecog: false,
    v2: false,
    fresh: false,
    status: null,
    trapDamage: '',
    lureRounds: '',
    stunBonus: 0,
    lastDamagedByTrack: null,
    turnResolved: false,
  };
};

const buildDefaultGagSetup = () =>
  GAG_TRACKS.map(() => ({
    maxLevel: null,
    maxPower: null,
  }));

const buildDefaultToon = (id) => ({
  id,
  name: 'Big Gus',
  maxLaff: 140,
  currentLaff: 140,
  gagSetup: buildDefaultGagSetup(),
  organicGags: [],
  gag: null,
  target: null,
  passSelected: false,
  turnResolved: false,
  unmaxed: false,
  highestGagLevel: null,
  gagPower: null,
  organic: false,
  accuracyOverride: false,
  accuracyOverrideBase: 100,
  accuracyOverrideTrack: 0,
});

const buildSquadGagSetup = (missingTracks = []) =>
  GAG_TRACKS.map((track) => {
    if (missingTracks.includes(track.name)) {
      return { maxLevel: 0, maxPower: null };
    }
    return { maxLevel: 7, maxPower: null };
  });

const buildSquadOrganicGags = (trackName, maxLevel = 7, missingTracks = []) => {
  if (!trackName || missingTracks.includes(trackName)) return [];
  const trackIndex = GAG_TRACKS.findIndex((track) => track.name === trackName);
  if (trackIndex < 0) return [];
  return Array.from({ length: maxLevel }, (_, idx) => `${trackIndex}-${idx}`);
};

const COG_BATTLE_SLOTS = 4;
const BATTLE_CARD_BASE_WIDTH = 240;
const BATTLE_CARD_BASE_GAP = 18;
const BATTLE_CARD_MIN_SCALE = 0.6;

function App() {
  const nextId = useRef(2);
  const nextToonId = useRef(2);
  const [battleLog, setBattleLog] = useState([]);
  const [battleLogHistory, setBattleLogHistory] = useState([]);
  const [logViewIndex, setLogViewIndex] = useState(null);
  const [cogs, setCogs] = useState(() => [buildDefaultCog(1)]);
  const [cogLineupMode, setCogLineupMode] = useState('custom');
  const [cogBattleType, setCogBattleType] = useState('cfo');
  const [battleCogPoolRemaining, setBattleCogPoolRemaining] = useState(0);
  const [battleCogPoolSize, setBattleCogPoolSize] = useState(10);
  const [battleCogEndless, setBattleCogEndless] = useState(false);
  const [toons, setToons] = useState(() => [buildDefaultToon(1)]);
  const [isSetupGagsOpen, setIsSetupGagsOpen] = useState(false);
  const [setupToonId, setSetupToonId] = useState(null);
  const [setupTrackIndex, setSetupTrackIndex] = useState(0);
  const [cogAccuracyConfig, setCogAccuracyConfig] = useState('max');
  const [cogUsageConfig, setCogUsageConfig] = useState('random');
  const [cogRandomSeed, setCogRandomSeed] = useState(0);
  const [cogTargetRandomSeed, setCogTargetRandomSeed] = useState(0);
  const [cogTargetConfig, setCogTargetConfig] = useState('random');
  const [lastTurnSnapshot, setLastTurnSnapshot] = useState(null);
  const [activeScreen, setActiveScreen] = useState('battle');
  const [turnSteps, setTurnSteps] = useState([]);
  const [turnMoves, setTurnMoves] = useState([]);
  const [turnHistory, setTurnHistory] = useState([]);
  const [turnHistoryIndex, setTurnHistoryIndex] = useState(-1);
  const [currentMoveIndex, setCurrentMoveIndex] = useState(-1);
  const [currentActionIndex, setCurrentActionIndex] = useState(-1);
  const [isMoveResolving, setIsMoveResolving] = useState(false);
  const [isTurnReadyToEnd, setIsTurnReadyToEnd] = useState(false);
  const [isBattleGagModalOpen, setIsBattleGagModalOpen] = useState(false);
  const [battleGagToonId, setBattleGagToonId] = useState(null);
  const [turnInProgress, setTurnInProgress] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [isTabsModalOpen, setIsTabsModalOpen] = useState(false);
  const [isModesModalOpen, setIsModesModalOpen] = useState(false);
  const [isCogEditOpen, setIsCogEditOpen] = useState(false);
  const [editCogId, setEditCogId] = useState(null);
  const [isToonEditOpen, setIsToonEditOpen] = useState(false);
  const [editToonId, setEditToonId] = useState(null);
  const [returnToonId, setReturnToonId] = useState(null);
  const [actionToasts, setActionToasts] = useState([]);
  const [defeatTipActive, setDefeatTipActive] = useState(false);
  const [battleCardScale, setBattleCardScale] = useState(1);
  const scrollPositionsRef = useRef({ battle: 0, cogs: 0, toons: 0 });
  const activeScreenRef = useRef(activeScreen);
  const autoAdvanceRef = useRef(false);
  const battleLogRef = useRef([]);
  const turnLogBaseRef = useRef([]);
  const turnHistoryIndexRef = useRef(-1);
  const lastAppliedStepRef = useRef(null);
  const moveTimerRef = useRef(null);
  const actionTimerRef = useRef(null);
  const logBodyRef = useRef(null);
  const toastIdRef = useRef(1);
  const toastTimersRef = useRef(new Map());
  const battleDisplayRef = useRef(null);

  const isViewingHistory = logViewIndex != null;
  const displayedLog = isViewingHistory
    ? battleLogHistory[logViewIndex]?.entries ?? []
    : battleLog;
  const isBattleCogMode = cogLineupMode === 'battle';

  const allToonsReady =
    toons.length > 0 &&
    toons.every(
      (toon) =>
        toon.passSelected || !toon.gag || (toon.gag && toon.target)
    );

  const buildBattleCog = (options = {}) => {
    const id = nextId.current;
    nextId.current += 1;
    const battleType = options.battleType ?? cogBattleType;
    const config = getBattleConfig(battleType);
    const level = getRandomIntInclusive(config.levelMin, config.levelMax);
    const eligibleCogs = (BATTLE_COG_VALUES[config.department] ?? []).filter(
      (value) => getLevelOptions(value).includes(level)
    );
    const value =
      eligibleCogs[Math.floor(Math.random() * eligibleCogs.length)] ??
      BATTLE_COG_VALUES[config.department]?.[0] ??
      COG_OPTIONS[0]?.value ??
      '';
    const levelValue = String(level);
    const maxHp = getMaxHp(levelValue);
    const isSkelecog =
      config.skelecogChance > 0 &&
      level >= config.skelecogMinLevel &&
      Math.random() < config.skelecogChance;
    const isV2 =
      !isSkelecog &&
      config.v2Chance > 0 &&
      Math.random() < config.v2Chance;
    return {
      ...buildDefaultCog(id, value),
      level: levelValue,
      currentHp: maxHp,
      defense: getCogDefense(levelValue, value),
      skelecog: isSkelecog,
      v2: isV2,
      fresh: Boolean(options.fresh),
      turnResolved: false,
    };
  };

  const initializeBattleCogs = ({
    battleType = cogBattleType,
    poolSize = battleCogPoolSize,
    endless = battleCogEndless,
  } = {}) => {
    const config = getBattleConfig(battleType);
    const initialCount = COG_BATTLE_SLOTS;
    const newCogs = Array.from({ length: initialCount }, () =>
      buildBattleCog({ fresh: false, battleType })
    );
    setCogs(newCogs);
    setBattleCogPoolRemaining(
      endless ? Number.POSITIVE_INFINITY : Math.max(poolSize, 0)
    );
  };

  const handleCogBattleTypeChange = (battleType) => {
    if (battleType === cogBattleType) return;
    setCogBattleType(battleType);
    setBattleCogEndless(false);
    setBattleCogPoolSize(10);
    setBattleCogPoolRemaining(10);
    if (cogLineupMode === 'battle') {
      initializeBattleCogs({ battleType, poolSize: 10, endless: false });
    }
    setToons((prev) =>
      prev.map((toon) => ({
        ...toon,
        gag: null,
        target: null,
        passSelected: false,
      }))
    );
  };

  const handleCogModeChange = (mode) => {
    if (mode === cogLineupMode) return;
    if (mode === 'battle') {
      setCogBattleType('cfo');
      setBattleCogPoolSize(10);
      setBattleCogEndless(false);
      setBattleCogPoolRemaining(10);
      initializeBattleCogs({
        battleType: 'cfo',
        poolSize: 10,
        endless: false,
      });
    } else {
      setCogs([buildDefaultCog(1)]);
      setBattleCogPoolRemaining(0);
    }
    setToons((prev) =>
      prev.map((toon) => ({
        ...toon,
        gag: null,
        target: null,
        passSelected: false,
      }))
    );
    setCogLineupMode(mode);
  };

  const handleBattlePoolSizeChange = (value) => {
    const nextValue = Number.parseInt(value, 10);
    const clamped = Number.isNaN(nextValue)
      ? 0
      : Math.min(Math.max(nextValue, 0), 99);
    setBattleCogPoolSize(clamped);
    if (!battleCogEndless) {
      setBattleCogPoolRemaining(clamped);
    }
  };

  const handleBattleEndlessChange = (checked) => {
    setBattleCogEndless(checked);
    if (checked) {
      setBattleCogPoolRemaining(Number.POSITIVE_INFINITY);
    } else {
      setBattleCogPoolRemaining(Math.max(battleCogPoolSize, 0));
    }
  };

  const canAnyToonBeDefeated = () => {
    const useResolved = turnInProgress;
    const isPendingToon = (toon) => !useResolved || !toon.turnResolved;
    const isPendingCog = (cog) => !useResolved || !cog.turnResolved;

    const toonUpActions = toons
      .map((toon) => {
        if (!isPendingToon(toon)) return null;
        if (toon.passSelected || !toon.gag || !toon.target) return null;
        const gagInfo = getGagInfo(toon.gag);
        if (!gagInfo || gagInfo.trackName !== 'Toon-Up') return null;
        const power = getGagPower(toon, gagInfo);
        if (power == null) return null;
        let targets = [];
        if (toon.target.type === 'toon') {
          targets = toons.filter((item) => item.id === toon.target.id);
        } else if (toon.target.type === 'all-toons-except-self') {
          targets = toons.filter((item) => item.id !== toon.id);
        }
        if (!targets.length) return null;
        const missHeal = Math.ceil(power * 0.2);
        const healPerTarget =
          toon.target.type === 'all-toons-except-self'
            ? Math.floor(missHeal / targets.length)
            : missHeal;
        if (!healPerTarget) return null;
        return { targets, healPerTarget };
      })
      .filter(Boolean);

    const healingByToonId = new Map();
    toonUpActions.forEach((action) => {
      action.targets.forEach((target) => {
        healingByToonId.set(
          target.id,
          (healingByToonId.get(target.id) ?? 0) + action.healPerTarget
        );
      });
    });

    const simToons = toons.map((toon) => {
      const heal = healingByToonId.get(toon.id) ?? 0;
      const currentLaff = toon.currentLaff ?? 0;
      return {
        ...toon,
        currentLaff: Math.min(toon.maxLaff, currentLaff + heal),
      };
    });

    const activeToons = simToons.filter((toon) => (toon.currentLaff ?? 0) > 0);
    if (!activeToons.length) return false;

    const trackOrder = {
      Trap: 1,
      Lure: 2,
      Sound: 3,
      Throw: 4,
      Squirt: 5,
      Drop: 6,
    };

    const gagActions = toons
      .map((toon, index) => {
        if (!isPendingToon(toon)) return null;
        if (toon.passSelected || !toon.gag || !toon.target) return null;
        const gagInfo = getGagInfo(toon.gag);
        if (!gagInfo) return null;
        if (gagInfo.trackName === 'Toon-Up') return null;
        if (!trackOrder[gagInfo.trackName]) return null;
        return {
          toonId: toon.id,
          toonOrder: index,
          gagInfo,
          trackName: gagInfo.trackName,
          target: toon.target,
          gagLevel: gagInfo.level ?? 0,
        };
      })
      .filter(Boolean);

    gagActions.sort(
      (a, b) =>
        trackOrder[a.trackName] - trackOrder[b.trackName] ||
        a.gagLevel - b.gagLevel ||
        a.toonOrder - b.toonOrder
    );

    const parseRounds = (cog) => {
      const roundsValue = Number.parseInt(cog.lureRounds, 10);
      return Number.isNaN(roundsValue) ? 0 : roundsValue;
    };

    const resolveTargets = (action, state) => {
      if (action.target?.type === 'all-cogs') return state;
      if (action.target?.type === 'cog') {
        return state.filter((cog) => cog.id === action.target.id);
      }
      return [];
    };

    const buildComponentsForIndices = (indices, actionTargets) => {
      if (!indices.length) return [];
      const parents = new Map(indices.map((index) => [index, index]));
      const findRoot = (index) => {
        const parent = parents.get(index);
        if (parent == null) return index;
        if (parent !== index) {
          const next = findRoot(parent);
          parents.set(index, next);
          return next;
        }
        return parent;
      };
      const union = (a, b) => {
        const rootA = findRoot(a);
        const rootB = findRoot(b);
        if (rootA !== rootB) parents.set(rootB, rootA);
      };
      for (let i = 0; i < indices.length; i += 1) {
        for (let j = i + 1; j < indices.length; j += 1) {
          const indexA = indices[i];
          const indexB = indices[j];
          const targetsA = actionTargets[indexA] ?? [];
          const targetsB = actionTargets[indexB] ?? [];
          const idsA = new Set(targetsA.map((cog) => cog.id));
          const idsB = new Set(targetsB.map((cog) => cog.id));
          let overlap = false;
          idsA.forEach((id) => {
            if (idsB.has(id)) overlap = true;
          });
          if (overlap) union(indexA, indexB);
        }
      }
      const componentMap = new Map();
      indices.forEach((index) => {
        const root = findRoot(index);
        const entry =
          componentMap.get(root) ?? { indices: [], targetIds: new Set() };
        entry.indices.push(index);
        (actionTargets[index] ?? []).forEach((cog) => entry.targetIds.add(cog.id));
        componentMap.set(root, entry);
      });
      return Array.from(componentMap.values());
    };

    const getComponentGuarantees = (trackName, component, state, options = {}) => {
      if (trackName === 'Trap') {
        return { guaranteedHit: true, guaranteedMiss: false };
      }
      if (!component.targetIds.size) {
        return { guaranteedHit: false, guaranteedMiss: true };
      }
      const hasLuredTarget = state.some(
        (cog) => component.targetIds.has(cog.id) && cog.status === 'lured'
      );
      if (trackName === 'Drop' && hasLuredTarget) {
        return { guaranteedHit: false, guaranteedMiss: true };
      }
      if (
        hasLuredTarget &&
        (trackName === 'Sound' ||
          trackName === 'Throw' ||
          trackName === 'Squirt')
      ) {
        const luredCount = state.filter((cog) => cog.status === 'lured').length;
        const totalCogs = state.length;
        const hasLuredRatioMix =
          totalCogs > 0 && luredCount > 0 && luredCount < totalCogs;
        const hasLuredRatioBonus =
          hasLuredRatioMix &&
          (trackName === 'Sound' ||
            (trackName === 'Throw' && options.hasWeddingCake));
        if (!hasLuredRatioBonus) {
          return { guaranteedHit: true, guaranteedMiss: false };
        }
      }
      return { guaranteedHit: false, guaranteedMiss: false };
    };

    const applyDamage = (cog, amount, pendingPromotions) => {
      const currentHp = cog.currentHp ?? getMaxHp(cog.level);
      const nextHp = Math.max(currentHp - amount, 0);
      if (nextHp <= 0 && cog.v2 && !cog.skelecog) {
        if (pendingPromotions) pendingPromotions.add(cog.id);
        return {
          ...cog,
          currentHp: 0,
        };
      }
      if (nextHp <= 0) {
        return null;
      }
      return { ...cog, currentHp: nextHp };
    };

    const applyGagHit = (
      action,
      state,
      targets,
      basePower,
      knockbackTargets,
      pendingPromotions
    ) => {
      const gagInfo = action.gagInfo;
      const trackName = action.trackName;
      if (!targets.length) return state;

      if (trackName === 'Trap') {
        const isRailroad = gagInfo.gagName === 'Railroad';
        return state.map((cog) => {
          if (!targets.some((target) => target.id === cog.id)) return cog;
          if (!isRailroad && cog.status) return cog;
          return {
            ...cog,
            status: 'trapped',
            trapDamage: String(basePower ?? ''),
            lureRounds: '',
          };
        });
      }

      if (trackName === 'Lure') {
        return state
          .map((cog) => {
            const target = targets.find((item) => item.id === cog.id);
            if (!target) return cog;
            if (cog.status === 'lured') return cog;
            if (cog.status === 'trapped') {
              const trapValue = Number.parseInt(cog.trapDamage, 10);
              const trapDamage = Number.isNaN(trapValue) ? 0 : trapValue;
              const damaged = applyDamage(cog, trapDamage, pendingPromotions);
              if (!damaged) return null;
              return {
                ...damaged,
                status: null,
                trapDamage: '',
                lureRounds: '',
              };
            }
            return {
              ...cog,
              status: 'lured',
              lureRounds: String(gagInfo.roundsLured ?? ''),
              trapDamage: '',
            };
          })
          .filter(Boolean);
      }

      if (!DAMAGE_GAG_TRACKS.has(trackName)) return state;
      if (basePower == null) return state;
      return state
        .map((cog) => {
          const target = targets.find((item) => item.id === cog.id);
          if (!target) return cog;
          const isLured = cog.status === 'lured';
          const isLuredForBonus = knockbackTargets?.has(cog.id) || isLured;
          const lureBonus =
            isLuredForBonus && (trackName === 'Throw' || trackName === 'Squirt')
              ? Math.floor((basePower ?? 0) * 0.5)
              : 0;
          const totalDamage = (basePower ?? 0) + lureBonus;
          const damaged = applyDamage(cog, totalDamage, pendingPromotions);
          if (!damaged) return null;
          const clearsLure =
            isLuredForBonus &&
            (trackName === 'Sound' ||
              trackName === 'Throw' ||
              trackName === 'Squirt');
          return {
            ...damaged,
            status: clearsLure ? null : damaged.status,
            lureRounds: clearsLure ? '' : damaged.lureRounds,
          };
        })
        .filter(Boolean);
    };

    let bestAttackerCount = -1;
    let bestCogsState = cogs.map(cloneCog);
    const trackGroups = gagActions.reduce((acc, action) => {
      const last = acc[acc.length - 1];
      if (last && last.trackName === action.trackName) {
        last.actions.push(action);
      } else {
        acc.push({ trackName: action.trackName, actions: [action] });
      }
      return acc;
    }, []);

    const evaluateAttackers = (simCogs) => {
      const attackers = simCogs.filter((cog) => {
        if ((cog.currentHp ?? getMaxHp(cog.level)) <= 0) return false;
        if (cog.fresh) return false;
        if (!isPendingCog(cog)) return false;
        if (cog.status !== 'lured') return true;
        const roundsRemaining = parseRounds(cog);
        return roundsRemaining <= 1;
      });
      if (attackers.length > bestAttackerCount) {
        bestAttackerCount = attackers.length;
        bestCogsState = simCogs.map(cloneCog);
      }
    };

    const simulateTrackGroup = (groupIndex, simCogs) => {
      if (groupIndex >= trackGroups.length) {
        evaluateAttackers(simCogs);
        return;
      }
      const group = trackGroups[groupIndex];
      const { trackName, actions } = group;
      const actionTargets = actions.map((action) =>
        resolveTargets(action, simCogs)
      );
      const luredAtStart = new Set(
        simCogs
          .filter((cog) => cog.status === 'lured')
          .map((cog) => cog.id)
      );

      const buildComponents = () => {
        if (trackName !== 'Throw') {
          const indices = actions.map((_, index) => index);
          return buildComponentsForIndices(indices, actionTargets).map(
            (component) => ({ ...component, hasWeddingCake: false })
          );
        }
        const cakeIndices = [];
        const otherIndices = [];
        actions.forEach((action, index) => {
          if (action.gagInfo.gagName === 'Wedding Cake') {
            cakeIndices.push(index);
          } else {
            otherIndices.push(index);
          }
        });
        const components = [];
        buildComponentsForIndices(cakeIndices, actionTargets).forEach(
          (component) => {
            components.push({ ...component, hasWeddingCake: true });
          }
        );
        buildComponentsForIndices(otherIndices, actionTargets).forEach(
          (component) => {
            components.push({ ...component, hasWeddingCake: false });
          }
        );
        return components;
      };

      const components = buildComponents();
      const actionToComponent = new Map();
      const componentKnockbackTargets = new Map();
      components.forEach((component, componentIndex) => {
        component.indices.forEach((index) => {
          actionToComponent.set(index, componentIndex);
        });
        if (trackName === 'Throw' || trackName === 'Squirt') {
          const targets = new Set();
          component.targetIds.forEach((id) => {
            if (luredAtStart.has(id)) targets.add(id);
          });
          if (targets.size) {
            componentKnockbackTargets.set(componentIndex, targets);
          }
        }
      });

      const componentGuarantees = components.map((component) =>
        getComponentGuarantees(trackName, component, simCogs, {
          hasWeddingCake: component.hasWeddingCake,
        })
      );
      const variableComponents = components
        .map((component, index) => ({ component, index }))
        .filter(
          ({ index }) =>
            !componentGuarantees[index].guaranteedHit &&
            !componentGuarantees[index].guaranteedMiss
        );
      const totalCombos = 1 << variableComponents.length;

      for (let mask = 0; mask < totalCombos; mask += 1) {
        const componentHits = new Map();
        components.forEach((component, index) => {
          const guarantee = componentGuarantees[index];
          if (guarantee.guaranteedHit) {
            componentHits.set(index, true);
          } else if (guarantee.guaranteedMiss) {
            componentHits.set(index, false);
          }
        });
        variableComponents.forEach(({ index }, bitIndex) => {
          componentHits.set(index, (mask & (1 << bitIndex)) !== 0);
        });

        let nextCogs = simCogs.map(cloneCog);
        const pendingPromotions = new Set();
        const comboMap = new Map();
        actions.forEach((action, actionIndex) => {
          const componentIndex = actionToComponent.get(actionIndex);
          if (componentIndex == null) return;
          if (!componentHits.get(componentIndex)) return;
          const targets = actionTargets[actionIndex] ?? [];
          const knockbackTargets =
            componentKnockbackTargets.get(componentIndex) ?? null;
          const basePower = getGagPower(
            toons.find((toon) => toon.id === action.toonId),
            action.gagInfo
          );
          nextCogs = applyGagHit(
            action,
            nextCogs,
            targets,
            basePower,
            knockbackTargets,
            pendingPromotions
          );
          if (DAMAGE_GAG_TRACKS.has(trackName) && basePower != null) {
            targets.forEach((target) => {
              if (!nextCogs.some((cog) => cog.id === target.id)) return;
              const existing = comboMap.get(target.id) ?? { sum: 0, count: 0 };
              comboMap.set(target.id, {
                sum: existing.sum + basePower,
                count: existing.count + 1,
              });
            });
          }
        });

        if (DAMAGE_GAG_TRACKS.has(trackName) && comboMap.size) {
          nextCogs = nextCogs
            .map((cog) => {
              const combo = comboMap.get(cog.id);
              if (!combo || combo.count < 2) return cog;
              const comboDamage = Math.floor(combo.sum * 0.2);
              const damaged = applyDamage(cog, comboDamage, pendingPromotions);
              if (!damaged) return null;
              return damaged;
            })
            .filter(Boolean);
        }

        if (pendingPromotions.size) {
          nextCogs = nextCogs.map((item) => {
            if (!pendingPromotions.has(item.id)) return item;
            return {
              ...item,
              currentHp: getMaxHp(item.level),
              v2: false,
              skelecog: true,
            };
          });
        }

        simulateTrackGroup(groupIndex + 1, nextCogs);
      }
    };

    if (trackGroups.length) {
      simulateTrackGroup(0, cogs.map(cloneCog));
    } else {
      evaluateAttackers(cogs.map(cloneCog));
    }

    if (bestAttackerCount <= 0) return false;

    const attackingCogs = bestCogsState.filter((cog) => {
      if ((cog.currentHp ?? getMaxHp(cog.level)) <= 0) return false;
      if (cog.fresh) return false;
      if (!isPendingCog(cog)) return false;
      if (cog.status !== 'lured') return true;
      const roundsRemaining = parseRounds(cog);
      return roundsRemaining <= 1;
    });
    if (!attackingCogs.length) return false;

    const defeatedIds = new Set();
    const combos = 1 << attackingCogs.length;
    for (let mask = 0; mask < combos; mask += 1) {
      let toonState = activeToons.map((toon) => ({
        id: toon.id,
        name: toon.name,
        currentLaff: toon.currentLaff ?? 0,
        maxLaff: toon.maxLaff,
      }));

      for (let idx = attackingCogs.length - 1; idx >= 0; idx -= 1) {
        const cog = attackingCogs[idx];
        const toonsAlive = toonState.filter((toon) => toon.currentLaff > 0);
        if (!toonsAlive.length) break;
        const useStrongestSingle = (mask & (1 << idx)) !== 0;
        const usage = useStrongestSingle ? 'strongest-single' : 'strongest';
        const attacks = getCogAttacks(cog.value, cog.level);
        if (!attacks) continue;
        const attackName = pickCogAttack(attacks, toonsAlive, usage, null, null);
        if (!attackName) continue;
        const attack = attacks[attackName];
        const damage = attack?.damage ?? 0;
        if (attack?.affects === 'multi') {
          toonState = toonState.map((toon) => ({
            ...toon,
            currentLaff: Math.max(toon.currentLaff - damage, 0),
          }));
        } else {
          const target = useStrongestSingle
            ? toonsAlive.reduce((weakest, toon) =>
                toon.currentLaff < weakest.currentLaff ? toon : weakest
              )
            : toonsAlive.reduce((strongest, toon) =>
                toon.currentLaff > strongest.currentLaff ? toon : strongest
              );
          toonState = toonState.map((toon) =>
            toon.id === target.id
              ? { ...toon, currentLaff: Math.max(toon.currentLaff - damage, 0) }
              : toon
          );
        }
      }

      toonState.forEach((toon) => {
        if (toon.currentLaff <= 0) {
          defeatedIds.add(toon.id);
        }
      });
      if (defeatedIds.size === activeToons.length) break;
    }

    return defeatedIds.size > 0;
  };

  useEffect(() => {
    const media = window.matchMedia('(max-width: 1100px)');
    const update = () => setIsMobileLayout(media.matches);
    update();
    if (media.addEventListener) {
      media.addEventListener('change', update);
    } else {
      media.addListener(update);
    }
    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', update);
      } else {
        media.removeListener(update);
      }
    };
  }, []);

  useEffect(() => {
    if (activeScreen !== 'battle') {
      setBattleCardScale(1);
      return;
    }
    if (isMobileLayout) {
      setBattleCardScale(1);
      return;
    }

    let observer;
    const updateScale = () => {
      const node = battleDisplayRef.current;
      if (!node) return;
      const styles = getComputedStyle(node);
      const paddingX =
        (Number.parseFloat(styles.paddingLeft) || 0) +
        (Number.parseFloat(styles.paddingRight) || 0);
      const available = node.clientWidth - paddingX;
      if (available <= 0) return;
      const count = Math.max(cogs.length, toons.length, 1);
      const required =
        count * BATTLE_CARD_BASE_WIDTH +
        (count - 1) * BATTLE_CARD_BASE_GAP;
      const ratio = available / required;
      const nextScale = Math.min(
        1,
        Math.max(BATTLE_CARD_MIN_SCALE, ratio)
      );
      setBattleCardScale((prev) =>
        Math.abs(prev - nextScale) < 0.01 ? prev : nextScale
      );
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    if (battleDisplayRef.current) {
      observer = new ResizeObserver(updateScale);
      observer.observe(battleDisplayRef.current);
    }
    return () => {
      window.removeEventListener('resize', updateScale);
      if (observer) observer.disconnect();
    };
  }, [activeScreen, isMobileLayout, cogs.length, toons.length]);

  useEffect(() => {
    if (
      !SHOW_TOON_DEFEAT_TIPS
    ) {
      setDefeatTipActive(false);
      return;
    }
    if (turnInProgress && isMoveResolving) {
      return;
    }
    const canCheckPreTurn = !turnInProgress;
    const canCheckMidTurn =
      turnInProgress && !isMoveResolving && !isTurnReadyToEnd;
    if (!cogs.length || !toons.length || (!canCheckPreTurn && !canCheckMidTurn)) {
      setDefeatTipActive(false);
      return;
    }
    setDefeatTipActive(canAnyToonBeDefeated());
  }, [
    SHOW_TOON_DEFEAT_TIPS,
    cogs,
    toons,
    turnInProgress,
    isMoveResolving,
    isTurnReadyToEnd,
  ]);

  useEffect(() => {
    if (battleCogEndless) return;
    if (!Number.isFinite(battleCogPoolRemaining)) return;
    if (battleCogPoolRemaining === battleCogPoolSize) return;
    setBattleCogPoolSize(battleCogPoolRemaining);
  }, [battleCogEndless, battleCogPoolRemaining, battleCogPoolSize]);

  const handleAddCog = () => {
    if (cogs.length >= 4) return;
    const newCog = buildDefaultCog(nextId.current);
    nextId.current += 1;
    setCogs((prev) => [...prev, newCog]);
  };

  const handleCopyCog = (id) => {
    if (cogs.length >= 4) return;
    const source = cogs.find((cog) => cog.id === id);
    if (!source) return;
    const newCog = { ...source, id: nextId.current };
    nextId.current += 1;
    setCogs((prev) => [...prev, newCog]);
  };

  const handleRemoveCog = (id) => {
    if (cogs.length <= 1) return;
    setCogs((prev) => prev.filter((cog) => cog.id !== id));
  };

  const handleCogChange = (id, value) => {
    const levelOptions = getLevelOptions(value);
    const defaultLevel = levelOptions.length > 0 ? String(levelOptions[0]) : '';
    const maxHp = getMaxHp(defaultLevel);
    const defense = getCogDefense(defaultLevel, value);
    setCogs((prev) =>
      prev.map((cog) =>
        cog.id === id
          ? {
              ...cog,
              value,
              level: defaultLevel,
              currentHp: maxHp,
              defense,
              status: null,
              trapDamage: '',
              lureRounds: '',
              stunBonus: 0,
              lastDamagedByTrack: null,
            }
          : cog
      )
    );
  };

  const handleLevelChange = (id, level) => {
    const maxHp = getMaxHp(level);
    setCogs((prev) =>
      prev.map((cog) =>
        cog.id === id
          ? {
              ...cog,
              level,
              currentHp: maxHp,
              defense: getCogDefense(level, cog.value),
            }
          : cog
      )
    );
  };

  const handleHpChange = (id, hp) => {
    const nextHp = Number.parseInt(hp, 10);
    setCogs((prev) =>
      prev.map((cog) =>
        cog.id === id
          ? { ...cog, currentHp: Number.isNaN(nextHp) ? 0 : nextHp }
          : cog
      )
    );
  };

  const handleSkelecogChange = (id, checked) => {
    setCogs((prev) =>
      prev.map((cog) =>
        cog.id === id
          ? { ...cog, skelecog: checked, v2: checked ? false : cog.v2 }
          : cog
      )
    );
  };

  const handleV2Change = (id, checked) => {
    setCogs((prev) =>
      prev.map((cog) =>
        cog.id === id
          ? { ...cog, v2: checked, skelecog: checked ? false : cog.skelecog }
          : cog
      )
    );
  };

  const handleCogStatusToggle = (id, status) => {
    setCogs((prev) =>
      prev.map((cog) => {
        if (cog.id !== id) return cog;
        if (cog.status === status) {
          return { ...cog, status: null, trapDamage: '', lureRounds: '' };
        }
        return {
          ...cog,
          status,
          trapDamage:
            status === 'trapped' ? (cog.trapDamage || '1') : '',
          lureRounds:
            status === 'lured' ? (cog.lureRounds || '1') : '',
        };
      })
    );
  };

  const handleTrapDamageChange = (id, value) => {
    setCogs((prev) =>
      prev.map((cog) =>
        cog.id === id ? { ...cog, trapDamage: value } : cog
      )
    );
  };

  const handleLureRoundsChange = (id, value) => {
    setCogs((prev) =>
      prev.map((cog) =>
        cog.id === id ? { ...cog, lureRounds: value } : cog
      )
    );
  };

  const handleAddToon = () => {
    if (toons.length >= 4) return;
    const newToon = buildDefaultToon(nextToonId.current);
    nextToonId.current += 1;
    setToons((prev) => [...prev, newToon]);
  };

  const handleSquadSetup = () => {
    const squad = [
      {
        id: 1,
        name: 'Big Gus',
        maxLaff: 140,
        missingTracks: ['Sound'],
        organicTrack: 'Lure',
      },
      {
        id: 2,
        name: 'Little Gus',
        maxLaff: 112,
        missingTracks: ['Lure'],
        organicTrack: 'Throw',
      },
      {
        id: 3,
        name: 'Cool Cat',
        maxLaff: 140,
        missingTracks: ['Toon-Up'],
        organicTrack: 'Throw',
      },
      {
        id: 4,
        name: 'Cool Cat Jr.',
        maxLaff: 134,
        missingTracks: ['Sound'],
        organicTrack: 'Trap',
      },
    ].map((config) => {
      const base = buildDefaultToon(config.id);
      return {
        ...base,
        name: config.name,
        maxLaff: config.maxLaff,
        currentLaff: config.maxLaff,
        gagSetup: buildSquadGagSetup(config.missingTracks),
        organicGags: buildSquadOrganicGags(
          config.organicTrack,
          7,
          config.missingTracks
        ),
      };
    });
    setToons(squad);
    nextToonId.current = 5;
  };

  const handleCopyToon = (id) => {
    if (toons.length >= 4) return;
    const source = toons.find((toon) => toon.id === id);
    if (!source) return;
    const newToon = {
      ...source,
      id: nextToonId.current,
      gag: source.gag ? { ...source.gag } : source.gag,
      target: source.target ? { ...source.target } : source.target,
      gagSetup: source.gagSetup
        ? source.gagSetup.map((entry) => ({ ...entry }))
        : source.gagSetup,
      organicGags: source.organicGags ? [...source.organicGags] : source.organicGags,
    };
    nextToonId.current += 1;
    setToons((prev) => [...prev, newToon]);
  };

  const handleRemoveToon = (id) => {
    if (toons.length <= 1) return;
    setToons((prev) => prev.filter((toon) => toon.id !== id));
  };

  const handleToonNameChange = (id, name) => {
    setToons((prev) =>
      prev.map((toon) => (toon.id === id ? { ...toon, name } : toon))
    );
  };

  const handleMaxLaffChange = (id, maxLaff) => {
    const nextMax = Number.parseInt(maxLaff, 10);
    const clampedMax = Number.isNaN(nextMax)
      ? 15
      : Math.min(Math.max(nextMax, 15), 140);
    setToons((prev) =>
      prev.map((toon) => {
        if (toon.id !== id) return toon;
        const nextCurrent = Math.min(
          Math.max(toon.currentLaff ?? clampedMax, 1),
          clampedMax
        );
        return { ...toon, maxLaff: clampedMax, currentLaff: nextCurrent };
      })
    );
  };

  const handleCurrentLaffChange = (id, currentLaff) => {
    const nextCurrent = Number.parseInt(currentLaff, 10);
    setToons((prev) =>
      prev.map((toon) =>
        toon.id === id
          ? { ...toon, currentLaff: Number.isNaN(nextCurrent) ? 1 : nextCurrent }
          : toon
      )
    );
  };

  const openBattleGagModal = (id) => {
    if (turnInProgress) return;
    setBattleGagToonId(id);
    setIsBattleGagModalOpen(true);
  };

  const closeBattleGagModal = () => {
    setIsBattleGagModalOpen(false);
    setBattleGagToonId(null);
  };

  const handleBattlePass = () => {
    if (!battleGagToonId) return;
    setToonPass(battleGagToonId);
    closeBattleGagModal();
  };

  const openSetupGagsModal = (id, options = {}) => {
    if (options.returnToEditor) {
      setReturnToonId(id);
    } else {
      setReturnToonId(null);
    }
    setSetupToonId(id);
    setSetupTrackIndex(0);
    setIsSetupGagsOpen(true);
    setToons((prev) =>
      prev.map((toon) =>
        toon.id === id
          ? {
              ...toon,
              gagSetup: buildDefaultGagSetup(),
              organicGags: [],
              gag: null,
              target: null,
              passSelected: false,
            }
          : toon
      )
    );
  };

  const closeSetupGagsModal = () => {
    setIsSetupGagsOpen(false);
    setSetupToonId(null);
    setSetupTrackIndex(0);
    if (returnToonId) {
      openToonEditor(returnToonId);
      setReturnToonId(null);
    }
  };

  const updateToonGagSetup = (toonId, trackIndex, data) => {
    setToons((prev) =>
      prev.map((toon) => {
        if (toon.id !== toonId) return toon;
        const currentSetup =
          toon.gagSetup && toon.gagSetup.length === GAG_TRACKS.length
            ? toon.gagSetup
            : buildDefaultGagSetup();
        const nextSetup = currentSetup.map((entry, index) =>
          index === trackIndex ? { ...entry, ...data } : entry
        );
        return { ...toon, gagSetup: nextSetup };
      })
    );
  };

  const getOrganicKey = (trackIndex, levelIndex) =>
    `${trackIndex}-${levelIndex}`;

  const advanceSetupTrack = () => {
    const nextIndex = setupTrackIndex + 1;
    if (nextIndex >= GAG_TRACKS.length) {
      setSetupTrackIndex(GAG_TRACKS.length);
      return;
    }
    setSetupTrackIndex(nextIndex);
  };

  const handleSetupNoTrack = () => {
    if (!setupToonId) return;
    updateToonGagSetup(setupToonId, setupTrackIndex, {
      maxLevel: 0,
      maxPower: null,
    });
    setToons((prev) =>
      prev.map((toon) => {
        if (toon.id !== setupToonId) return toon;
        if (!toon.organicGags?.length) return toon;
        return {
          ...toon,
          organicGags: toon.organicGags.filter((key) => {
            const [track] = key.split('-');
            return Number.parseInt(track, 10) !== setupTrackIndex;
          }),
        };
      })
    );
    advanceSetupTrack();
  };

  const handleSetupGagSelect = (row, col) => {
    if (!setupToonId) return;
    if (row !== setupTrackIndex) return;
    const track = GAG_TRACKS[row];
    const gagInfo = track?.gags?.[col];
    if (!gagInfo) return;
    const gagLevel = col + 1;
    const isLure = track.name === 'Lure';
    const min = gagInfo.min ?? null;
    const max = gagInfo.max ?? null;
    const setupToon = toons.find((toon) => toon.id === setupToonId);
    const existing = setupToon?.gagSetup?.[row];
    const defaultPower =
      existing && existing.maxLevel === gagLevel && existing.maxPower != null
        ? existing.maxPower
        : max ?? min ?? null;
    updateToonGagSetup(setupToonId, row, {
      maxLevel: gagLevel,
      maxPower: isLure ? null : defaultPower,
    });
    setToons((prev) =>
      prev.map((toon) => {
        if (toon.id !== setupToonId) return toon;
        if (!toon.organicGags?.length) return toon;
        return {
          ...toon,
          organicGags: toon.organicGags.filter((key) => {
            const [track, level] = key.split('-');
            if (Number.parseInt(track, 10) !== row) return true;
            const parsedLevel = Number.parseInt(level, 10);
            return Number.isNaN(parsedLevel) || parsedLevel < gagLevel;
          }),
        };
      })
    );
    advanceSetupTrack();
  };

  const handleSetupOrganicToggle = (row, col) => {
    if (!setupToonId) return;
    const setupToon = toons.find((toon) => toon.id === setupToonId);
    const maxLevel = setupToon?.gagSetup?.[row]?.maxLevel ?? 0;
    if (!maxLevel || col + 1 > maxLevel) return;
    const key = getOrganicKey(row, col);
    setToons((prev) =>
      prev.map((toon) => {
        if (toon.id !== setupToonId) return toon;
        const organicList = toon.organicGags ? [...toon.organicGags] : [];
        const organicSet = new Set(organicList);
        const exists = organicSet.has(key);
        let nextOrganic = organicList;
        if (exists) {
          nextOrganic = organicList.filter((item) => {
            const [track, level] = item.split('-');
            if (Number.parseInt(track, 10) !== row) return true;
            const parsedLevel = Number.parseInt(level, 10);
            return Number.isNaN(parsedLevel) || parsedLevel < col;
          });
        } else {
          const requiredKeys = Array.from({ length: col + 1 }, (_, idx) =>
            getOrganicKey(row, idx)
          );
          const missingKeys = requiredKeys.filter(
            (required) => !organicSet.has(required)
          );
          if (organicSet.size + missingKeys.length > 8) {
            return toon;
          }
          nextOrganic = Array.from(new Set([...organicList, ...missingKeys]));
        }

        const maxIndex = maxLevel - 1;
        const maxKey = getOrganicKey(row, maxIndex);
        const wasOrganicMax = organicList.includes(maxKey);
        const isOrganicMax = nextOrganic.includes(maxKey);
        let nextGagSetup = toon.gagSetup;
        if (wasOrganicMax !== isOrganicMax) {
          const gagInfo = GAG_TRACKS[row]?.gags?.[maxIndex];
          if (
            gagInfo &&
            gagInfo.min != null &&
            gagInfo.max != null &&
            gagInfo.min !== gagInfo.max
          ) {
            const rangeMax =
              isOrganicMax && gagInfo.organicMax != null
                ? gagInfo.organicMax
                : gagInfo.max;
            nextGagSetup = toon.gagSetup?.map((entry, index) =>
              index === row ? { ...entry, maxPower: rangeMax } : entry
            );
          }
        }
        return {
          ...toon,
          organicGags: nextOrganic,
          gagSetup: nextGagSetup,
        };
      })
    );
  };

  const handleSetupOrganicDone = () => {
    closeSetupGagsModal();
  };

  const handleBattleGagSelect = (trackIndex, levelIndex) => {
    if (!battleGagToonId) return;
    const targetToon = toons.find((toon) => toon.id === battleGagToonId);
    if (!targetToon) return;
    const setupEntry = targetToon.gagSetup?.[trackIndex];
    const maxLevel = setupEntry?.maxLevel ?? 0;
    if (!maxLevel || levelIndex + 1 > maxLevel) return;
    if (trackIndex === 0 && toons.length < 2) return;
    if (trackIndex === 1) {
      const anyTrapped = cogs.some((cog) => cog.status === 'trapped');
      const anyAvailable = cogs.some((cog) => !cog.status);
      const allLured =
        cogs.length > 0 && cogs.every((cog) => cog.status === 'lured');
      const gagName = GAG_TRACKS[trackIndex]?.gags?.[levelIndex]?.name;
      if (gagName === 'Railroad' && (anyTrapped || allLured)) return;
      if (gagName !== 'Railroad' && !anyAvailable) return;
    }
    if (trackIndex === 2) {
      const anyAvailable = cogs.some((cog) => cog.status !== 'lured');
      if (!anyAvailable) return;
    }
    const nextGag = { track: trackIndex + 1, level: levelIndex + 1 };
    const gagInfo = getGagInfo(nextGag);
    const organicKey = getOrganicKey(trackIndex, levelIndex);
    const organic = targetToon.organicGags?.includes(organicKey) ?? false;
    const unmaxed = maxLevel > 0 && maxLevel < 7;
    const highestGagLevel = unmaxed ? maxLevel : null;
    const gagPower =
      unmaxed && maxLevel === levelIndex + 1 && gagInfo?.min !== gagInfo?.max
        ? setupEntry?.maxPower ?? gagInfo?.max ?? null
        : null;
    const autoTarget = getAutoTarget(
      { ...targetToon, gag: nextGag },
      toons,
      cogs
    );
    setToons((prev) =>
      prev.map((toon) =>
        toon.id === battleGagToonId
          ? {
              ...toon,
              gag: nextGag,
              target: autoTarget,
              passSelected: false,
              turnResolved: false,
              unmaxed,
              highestGagLevel,
              gagPower,
              organic,
              accuracyOverride: false,
              accuracyOverrideBase: 100,
              accuracyOverrideTrack: 0,
            }
          : toon
      )
    );
    closeBattleGagModal();
  };

  const handleTargetSelect = (toonId, target) => {
    setToons((prev) =>
      prev.map((toon) =>
        toon.id === toonId ? { ...toon, target } : toon
      )
    );
  };

  const getAccuracyBreakdown = (toon, gagInfo) => {
    if (!gagInfo?.accuracy) return null;
    if (SHOW_MANUAL_GAG_ACCURACY_SLIDER && toon.accuracyOverride) {
      const baseValue = Number.parseInt(toon.accuracyOverrideBase, 10);
      const trackValue = Number.parseInt(toon.accuracyOverrideTrack, 10);
      const base = Number.isNaN(baseValue) ? 0 : baseValue;
      const trackBonus = Number.isNaN(trackValue) ? 0 : trackValue;
      return {
        total: base + trackBonus,
        base,
        trackBonus,
        organicBonus: 0,
        override: true,
      };
    }
    const base = gagInfo.accuracy ?? 0;
    if (gagInfo.trackName === 'Trap') {
      return {
        total: base,
        base,
        trackBonus: 0,
        organicBonus: 0,
        override: false,
      };
    }
    const gagLevel = toon.gag?.level ?? null;
    const effectiveHighestLevel = toon.gag
      ? toon.unmaxed
        ? Math.min(toon.highestGagLevel ?? gagLevel ?? 1, 7)
        : 7
      : null;
    const accuracyStep = gagInfo.trackName === 'Toon-Up' ? 5 : 10;
    const trackBonus =
      Math.max((effectiveHighestLevel ?? 1) - 1, 0) * accuracyStep;
    const organicBonus =
      toon.organic && gagInfo.trackName === 'Lure'
        ? gagInfo.organicAccuracyBonus ?? 0
        : 0;
    return {
      total: base + trackBonus + organicBonus,
      base,
      trackBonus,
      organicBonus,
      override: false,
    };
  };

  const getToonAccuracy = (toon, gagInfo) => {
    const breakdown = getAccuracyBreakdown(toon, gagInfo);
    return breakdown ? breakdown.total : null;
  };

  const getCogAccuracyChance = (configId) =>
    COG_ACCURACY_OPTIONS.find((option) => option.id === configId)?.chance ?? 0;

  const pickCogTargetForSimulation = (toonList, config) => {
    if (!toonList.length) return null;
    if (config === 'random') {
      return toonList[Math.floor(Math.random() * toonList.length)]?.id ?? null;
    }
    const scores = toonList.map((toon) => toon.currentLaff ?? 0);
    const targetValue =
      config === 'weakest' ? Math.min(...scores) : Math.max(...scores);
    const candidates = toonList.filter(
      (toon) => (toon.currentLaff ?? 0) === targetValue
    );
    return candidates.length
      ? candidates[Math.floor(Math.random() * candidates.length)].id
      : toonList[0]?.id ?? null;
  };

  const clearToonGagData = (toon) => ({
    ...toon,
    gag: null,
    target: null,
    passSelected: false,
    turnResolved: true,
    unmaxed: false,
    highestGagLevel: null,
    gagPower: null,
    organic: false,
    accuracyOverride: false,
    accuracyOverrideBase: 100,
    accuracyOverrideTrack: 0,
  });

  const clearToonGag = (toonId) => {
    setToons((prev) =>
      prev.map((toon) =>
        toon.id === toonId
          ? {
              ...toon,
              gag: null,
              target: null,
              passSelected: false,
              turnResolved: false,
              unmaxed: false,
              highestGagLevel: null,
              gagPower: null,
              organic: false,
              accuracyOverride: false,
              accuracyOverrideBase: 100,
              accuracyOverrideTrack: 0,
            }
          : toon
      )
    );
  };

  const setToonPass = (toonId) => {
    setToons((prev) =>
      prev.map((toon) =>
        toon.id === toonId
          ? {
              ...toon,
              gag: null,
              target: null,
              passSelected: true,
              turnResolved: false,
              unmaxed: false,
              highestGagLevel: null,
              gagPower: null,
              organic: false,
              accuracyOverride: false,
              accuracyOverrideBase: 100,
              accuracyOverrideTrack: 0,
            }
          : toon
      )
    );
  };

  const buildAccuracySummary = (breakdown, extras = []) => {
    if (!breakdown) return null;
    const extraTotal = extras.reduce(
      (sum, item) => sum + (item?.value ?? 0),
      0
    );
    const total = Math.round((breakdown.total ?? 0) + extraTotal);
    const parts = [];
    if (breakdown.base != null) {
      parts.push(`${Math.round(breakdown.base)}% base`);
    }
    if (breakdown.trackBonus) {
      parts.push(`${Math.round(breakdown.trackBonus)}% track EXP`);
    }
    if (breakdown.organicBonus) {
      parts.push(`${Math.round(breakdown.organicBonus)}% organic`);
    }
    extras.forEach((item) => {
      if (item?.value) {
        parts.push(`${Math.round(item.value)}% ${item.label}`);
      }
    });
    return {
      total,
      parts,
      text: parts.length ? `${parts.join(' + ')} = ${total}% total` : `${total}% total`,
      override: breakdown.override,
    };
  };

  const runTurnSimulation = ({
    recordSteps = false,
    startingCogs = cogs,
    startingToons = toons,
    startingBattlePool = battleCogPoolRemaining,
    battleMode = isBattleCogMode,
    skipTurnStart = false,
    preserveResolved = false,
  } = {}) => {
    const log = [];
    const steps = [];
    const trackOrder = [
      'Toon-Up',
      'Trap',
      'Lure',
      'Sound',
      'Throw',
      'Squirt',
      'Drop',
    ];
    let nextCogs = startingCogs.map((cog) => ({
      ...cog,
      turnResolved: preserveResolved ? cog.turnResolved ?? false : false,
    }));
    let nextToons = startingToons.map((toon) => ({
      ...toon,
      turnResolved: preserveResolved ? toon.turnResolved ?? false : false,
    }));
    let nextBattlePool = startingBattlePool ?? 0;
    const commitStep = (toasts = []) => {
      if (!recordSteps) return;
      const toastList = Array.isArray(toasts)
        ? toasts.filter(Boolean)
        : toasts
          ? [toasts]
          : [];
      const dedupedToasts = [];
      if (toastList.length) {
        const seen = new Set();
        for (let i = toastList.length - 1; i >= 0; i -= 1) {
          const toast = toastList[i];
          if (!toast) continue;
          const key = `${toast.targetType}-${toast.targetId}`;
          if (seen.has(key)) continue;
          seen.add(key);
          dedupedToasts.push(toast);
        }
        dedupedToasts.reverse();
      }
      steps.push({
        battleLog: [...log],
        cogs: nextCogs.map(cloneCog),
        toons: nextToons.map(cloneToon),
        battleCogPoolRemaining: nextBattlePool,
        toasts: toastList.length ? dedupedToasts : toastList,
      });
    };
    const logLine = (text, toasts = []) => {
      log.push(text);
      commitStep(toasts);
    };
    const logLines = (lines, toasts = []) => {
      const entries = Array.isArray(lines) ? lines.filter(Boolean) : [lines].filter(Boolean);
      if (!entries.length) return;
      entries.forEach((entry) => log.push(entry));
      commitStep(toasts);
    };
    const toonLabel = (toon) => {
      const index = nextToons.findIndex((item) => item.id === toon.id);
      return `Toon #${index + 1} (${toon.name})`;
    };
    const getToonOrder = (toon) =>
      Math.max(nextToons.findIndex((item) => item.id === toon.id), 0);
    const cogLabel = (cog) => {
      const index = nextCogs.findIndex((item) => item.id === cog.id);
      return `Cog #${index + 1} (${getCogLabel(cog.value)})`;
    };
    const getStunBonus = (cogId) =>
      nextCogs.find((cog) => cog.id === cogId)?.stunBonus ?? 0;
    const getMaxStunBonus = (targets) =>
      targets.length ? Math.max(...targets.map((cog) => getStunBonus(cog.id))) : 0;
    const getCogDefenseValue = (cog) =>
      cog.defense ?? getCogDefense(cog.level, cog.value);
    const markCogResolved = (cogId) => {
      nextCogs = nextCogs.map((item) =>
        item.id === cogId ? { ...item, turnResolved: true } : item
      );
    };
    const getMaxDefense = (targets) =>
      targets.length ? Math.max(...targets.map((cog) => getCogDefenseValue(cog))) : 0;
    const resetStunBonus = (cogId) => {
      const current = nextCogs.find((cog) => cog.id === cogId);
      if (!current || !current.stunBonus) return;
      nextCogs = nextCogs.map((cog) =>
        cog.id === cogId ? { ...cog, stunBonus: 0 } : cog
      );
    };
    const applyStunHit = (cogId, trackName, bonusAmount) => {
      const current = nextCogs.find((cog) => cog.id === cogId);
      if (!current || current.lastDamagedByTrack === trackName) return;
      nextCogs = nextCogs.map((cog) =>
        cog.id === cogId
          ? {
              ...cog,
              stunBonus: (cog.stunBonus ?? 0) + bonusAmount,
              lastDamagedByTrack: trackName,
            }
          : cog
      );
    };
    const describeAccuracy = (effectiveChance, rawChance, includeRaw = true) => {
      const raw = Math.round(rawChance ?? effectiveChance ?? 0);
      const effective = Math.round(effectiveChance ?? 0);
      if (includeRaw && raw > ACCURACY_CAP) {
        return `${effective}% accuracy (${raw}% raw)`;
      }
      return `${effective}% accuracy`;
    };
    const createToast = (targetType, targetId, text, tone = 'damage', options = {}) => ({
      id: toastIdRef.current++,
      targetType,
      targetId,
      text,
      tone,
      icon: options.icon ?? null,
    });

    if (!skipTurnStart) {
      logLine('Turn start');
    }

    const passToons = nextToons.filter(
      (toon) => !toon.turnResolved && (toon.passSelected || !toon.gag)
    );
    if (passToons.length) {
      nextToons = nextToons.map((toon) =>
        passToons.some((passer) => passer.id === toon.id)
          ? { ...toon, turnResolved: true }
          : toon
      );
      logLines(passToons.map((toon) => `${toonLabel(toon)} passed.`));
    }

    for (const trackName of trackOrder) {
      if (!nextCogs.length || !nextToons.length) break;
      const actingToons = nextToons.filter(
        (toon) =>
          !toon.turnResolved &&
          getGagInfo(toon.gag)?.trackName === trackName
      );

      if (trackName === 'Trap') {
        const trapActions = actingToons
          .map((toon) => {
            const gagInfo = getGagInfo(toon.gag);
            if (!gagInfo || !toon.target) return null;
            const power = getGagPower(toon, gagInfo);
            if (power == null) return null;
            const targetIds =
              toon.target?.type === 'all-cogs'
                ? nextCogs.map((cog) => cog.id)
                : toon.target?.type === 'cog'
                  ? [toon.target.id]
                  : [];
            return {
              toon,
              gagInfo,
              gagLevel: gagInfo.level ?? 0,
              toonOrder: getToonOrder(toon),
              power,
              targetIds,
            };
          })
          .filter(Boolean);

        if (trapActions.length) {
          trapActions.sort(
            (a, b) => a.gagLevel - b.gagLevel || a.toonOrder - b.toonOrder
          );
          const targetCounts = new Map();
          trapActions.forEach((action) => {
            action.targetIds.forEach((id) => {
              targetCounts.set(id, (targetCounts.get(id) ?? 0) + 1);
            });
          });

          const poofActions = new Set(
            trapActions
              .filter((action) =>
                action.targetIds.some((id) => (targetCounts.get(id) ?? 0) > 1)
              )
              .map((action) => action.toon.id)
          );

          const usageLines = [];
          const effectLines = [];
          const effectToasts = [];

          trapActions.forEach((action) => {
            const { toon, gagInfo, power, targetIds } = action;
            const actor = toonLabel(toon);
            const isRailroad = gagInfo.gagName === 'Railroad';
            const hasPoofed = poofActions.has(toon.id);
            if (toon.target?.type === 'all-cogs') {
              nextToons = nextToons.map((item) =>
                item.id === toon.id ? clearToonGagData(item) : item
              );
              usageLines.push(`${actor} used ${gagInfo.gagName}.`);
            } else if (toon.target?.type === 'cog') {
              const targetCog = nextCogs.find((cog) => cog.id === targetIds[0]);
              if (targetCog) {
                nextToons = nextToons.map((item) =>
                  item.id === toon.id ? clearToonGagData(item) : item
                );
                usageLines.push(
                  `${actor} used ${gagInfo.gagName} on ${cogLabel(targetCog)}.`
                );
              }
            }
            if (hasPoofed) {
              effectLines.push(`${actor}'s trap poofed.`);
              return;
            }
            targetIds.forEach((targetId) => {
              const targetCog = nextCogs.find((cog) => cog.id === targetId);
              if (!targetCog) return;
              nextCogs = nextCogs.map((cog) =>
                cog.id === targetId
                  ? {
                      ...cog,
                      status: 'trapped',
                      trapDamage: String(power),
                      lureRounds: isRailroad ? '' : cog.lureRounds,
                    }
                  : cog
              );
              effectLines.push(
                `${cogLabel(targetCog)} is trapped for ${power} damage.`
              );
              effectToasts.push(
                createToast('cog', targetCog.id, '', 'status', {
                  icon: trappedIcon,
                })
              );
            });
          });

          logLines(usageLines);
          logLines(effectLines, effectToasts);
        }
        continue;
      }

      if (trackName === 'Lure') {
        const getLureCategory = (action) =>
          action.gagInfo.targeting === 'all-cogs' ? 'group' : 'single';
        const getLureCategoryIndex = (action) => {
          const level = action.gagInfo.level ?? 0;
          if (action.gagInfo.targeting === 'all-cogs') {
            if (level === 2) return 1;
            if (level === 4) return 2;
            if (level === 6) return 3;
            if (level === 7) return 4;
          } else {
            if (level === 1) return 1;
            if (level === 3) return 2;
            if (level === 5) return 3;
          }
          return level;
        };
        const lureActions = actingToons
          .map((toon) => {
            const gagInfo = getGagInfo(toon.gag);
            if (!gagInfo || !toon.target) return null;
            const accuracyBreakdown = getAccuracyBreakdown(toon, gagInfo);
            const rawAccuracy = accuracyBreakdown?.total ?? 0;
            const chance = clampAccuracyValue(rawAccuracy);
            const targets =
              toon.target?.type === 'all-cogs'
                ? nextCogs.filter((cog) => cog.status !== 'lured')
                : toon.target?.type === 'cog'
                  ? nextCogs.filter(
                      (cog) => cog.id === toon.target?.id && cog.status !== 'lured'
                    )
                  : [];
            return {
              toon,
              gagInfo,
              gagLevel: gagInfo.level ?? 0,
              toonOrder: getToonOrder(toon),
              targets,
              chance,
              rawAccuracy,
              accuracyBreakdown,
              rounds: gagInfo.roundsLured ?? 0,
            };
          })
          .filter(Boolean);

        if (lureActions.length) {
          lureActions.sort(
            (a, b) => a.gagLevel - b.gagLevel || a.toonOrder - b.toonOrder
          );
          const actionMeta = lureActions.map((action) => {
            const category = getLureCategory(action);
            const categoryIndex = getLureCategoryIndex(action);
            const targetKey =
              category === 'single' && action.targets.length === 1
                ? action.targets[0].id
                : category === 'group'
                  ? 'group'
                  : null;
            return { action, category, categoryIndex, targetKey };
          });
          const lureBonusByToon = new Map();
          actionMeta.forEach(({ action, category, categoryIndex, targetKey }, index) => {
            let bonus = 0;
            if (targetKey == null) {
              lureBonusByToon.set(action.toon.id, bonus);
              return;
            }
            actionMeta.forEach((other, otherIndex) => {
              if (index === otherIndex) return;
              if (category !== other.category) return;
              if (targetKey !== other.targetKey) return;
              const levelDiff = Math.abs(
                (categoryIndex ?? 0) - (other.categoryIndex ?? 0)
              );
              const pairBonus = Math.max(20 - levelDiff * 5, 10);
              bonus += pairBonus;
            });
            lureBonusByToon.set(action.toon.id, bonus);
          });

          const trapBonusByToon = new Map();
          actionMeta.forEach(({ action }) => {
            const hasTrappedTarget = action.targets.some(
              (cog) => cog.status === 'trapped'
            );
            trapBonusByToon.set(action.toon.id, hasTrappedTarget ? 10 : 0);
          });

          const stunBonusByToon = new Map();
          actionMeta.forEach(({ action }) => {
            const bonus = getMaxStunBonus(action.targets);
            stunBonusByToon.set(action.toon.id, bonus);
          });

          const defenseByToon = new Map();
          actionMeta.forEach(({ action }) => {
            const defense = getMaxDefense(action.targets);
            defenseByToon.set(action.toon.id, defense);
          });

          const totalAccuracies = lureActions.map((action) => {
            const lureBonus = lureBonusByToon.get(action.toon.id) ?? 0;
            const trapBonus = trapBonusByToon.get(action.toon.id) ?? 0;
            const stunBonus = stunBonusByToon.get(action.toon.id) ?? 0;
            const defense = defenseByToon.get(action.toon.id) ?? 0;
            return (
              (action.rawAccuracy ?? 0) +
              lureBonus +
              trapBonus +
              stunBonus -
              defense
            );
          });
          const highestRawAccuracy = Math.max(...totalAccuracies);
          const highestAccuracy = clampAccuracyValue(highestRawAccuracy);
          const isHit = rollChance(highestAccuracy);
          const sharedText = lureActions.length > 1 ? 'shared roll, ' : '';

          const usageLines = [];
          const dodgeLines = [];
          const dodgeToasts = [];
          lureActions.forEach((action) => {
            const targetCog =
              action.targets.length === 1 ? action.targets[0] : null;
            const targetText =
              action.targets.length > 1
                ? ''
                : targetCog
                  ? `on ${cogLabel(targetCog)}`
                  : '';
            const lureBonus = lureBonusByToon.get(action.toon.id) ?? 0;
            const trapBonus = trapBonusByToon.get(action.toon.id) ?? 0;
            const stunBonus = stunBonusByToon.get(action.toon.id) ?? 0;
            const defense = defenseByToon.get(action.toon.id) ?? 0;
            const accuracySummary = buildAccuracySummary(action.accuracyBreakdown, [
              { label: 'Lure bonus', value: lureBonus },
              { label: 'Trap bonus', value: trapBonus },
              { label: 'Stun bonus', value: stunBonus },
              { label: 'Defense', value: defense ? -defense : 0 },
            ]);
            const actionTotal = accuracySummary?.total ?? 0;
            const sharedDetail =
              lureActions.length > 1 && actionTotal !== highestRawAccuracy
                ? ` (max ${Math.round(highestRawAccuracy)}% total)`
                : '';
            nextToons = nextToons.map((item) =>
              item.id === action.toon.id ? clearToonGagData(item) : item
            );
            usageLines.push(
              `${toonLabel(action.toon)} used ${action.gagInfo.gagName}${
                targetText ? ` ${targetText}` : ''
              }. ${isHit ? 'Hit' : 'Missed'} (${sharedText}${describeAccuracy(
                highestAccuracy,
                highestRawAccuracy,
                false
              )}${accuracySummary ? `; ${accuracySummary.text}` : ''}${sharedDetail}).`
            );
            if (!isHit) {
              action.targets.forEach((cog) => {
                dodgeLines.push(`${cogLabel(cog)} dodged.`);
                dodgeToasts.push(
                  createToast('cog', cog.id, 'MISSED', 'miss')
                );
              });
            }
          });
          logLines(usageLines);
          if (!isHit && dodgeLines.length) {
            logLines(dodgeLines, dodgeToasts);
          }

          if (isHit) {
            const roundsByCog = new Map();
            lureActions.forEach((action) => {
              action.targets.forEach((cog) => {
                const previous = roundsByCog.get(cog.id) ?? 0;
                const nextValue = Math.max(previous, action.rounds);
                roundsByCog.set(cog.id, nextValue);
              });
            });

            const statusLines = [];
            const statusToasts = [];
            const defeatedIds = new Set();
            const defeatedLines = [];
            const layerDestroyedIds = new Set();
            const layerDestroyedLines = [];

            nextCogs.forEach((cog) => {
              const roundsValue = roundsByCog.get(cog.id);
              if (roundsValue == null) return;
              const label = cogLabel(cog);
            if (cog.status === 'trapped') {
              const trapDamageValue = Number.parseInt(cog.trapDamage, 10);
              const trapDamage = Number.isNaN(trapDamageValue)
                ? 0
                : trapDamageValue;
              const currentHp = cog.currentHp ?? getMaxHp(cog.level);
              const nextHp = Math.max(currentHp - trapDamage, 0);
              const shouldPromote = nextHp <= 0 && cog.v2 && !cog.skelecog;
              nextCogs = nextCogs.map((item) =>
                item.id === cog.id
                  ? {
                      ...item,
                      currentHp: nextHp,
                      v2: item.v2,
                      skelecog: item.skelecog,
                      status: null,
                      trapDamage: '',
                      lureRounds: '',
                    }
                  : item
              );
                statusLines.push(
                  `${label} triggered its trap for ${trapDamage} damage.`
                );
                statusToasts.push(
                  createToast('cog', cog.id, `-${trapDamage}`, 'damage')
                );
                if (shouldPromote) {
                  layerDestroyedIds.add(cog.id);
                  layerDestroyedLines.push(`${label} v2.0 layer destroyed.`);
                  applyStunHit(cog.id, 'Trap', 30);
                } else if (nextHp > 0) {
                  applyStunHit(cog.id, 'Trap', 30);
                } else {
                  defeatedIds.add(cog.id);
                  defeatedLines.push(`${label} was defeated!`);
                }
                return;
              }
              if (cog.status === 'lured') {
                return;
              }
              nextCogs = nextCogs.map((item) =>
                item.id === cog.id
                  ? {
                      ...item,
                      status: 'lured',
                      lureRounds: String(roundsValue),
                      trapDamage: '',
                    }
                  : item
              );
              statusLines.push(`${label} is lured for ${roundsValue} rounds.`);
              statusToasts.push(
                createToast('cog', cog.id, `x${roundsValue}`, 'status', {
                  icon: luredIcon,
                })
              );
            });

            logLines(statusLines, statusToasts);
            if (layerDestroyedIds.size) {
              nextCogs = nextCogs.map((item) =>
                layerDestroyedIds.has(item.id)
                  ? {
                      ...item,
                      currentHp: getMaxHp(item.level),
                      v2: false,
                      skelecog: true,
                    }
                  : item
              );
            }
            logLines(layerDestroyedLines);
            if (defeatedIds.size) {
              nextCogs = nextCogs.filter((item) => !defeatedIds.has(item.id));
              logLines(defeatedLines);
            }
          } else {
            const missedIds = new Set();
            lureActions.forEach((action) => {
              action.targets.forEach((cog) => missedIds.add(cog.id));
            });
            missedIds.forEach((cogId) => resetStunBonus(cogId));
          }

        }
        continue;
      }

      if (DAMAGE_GAG_TRACKS.has(trackName)) {
        const damageActions = actingToons
          .map((toon) => {
            const gagInfo = getGagInfo(toon.gag);
            if (!gagInfo || !toon.target) return null;
            const power = getGagPower(toon, gagInfo);
            if (power == null) return null;
            const accuracyBreakdown = getAccuracyBreakdown(toon, gagInfo);
            const rawAccuracy = accuracyBreakdown?.total ?? 0;
            const targets =
              toon.target?.type === 'all-cogs'
                ? nextCogs
                : toon.target?.type === 'cog'
                  ? nextCogs.filter((cog) => cog.id === toon.target?.id)
                  : [];
            return {
              id: toon.id,
              toon,
              gagInfo,
              gagLevel: gagInfo.level ?? 0,
              toonOrder: getToonOrder(toon),
              power,
              targets,
              rawAccuracy,
              accuracyBreakdown,
              stunBonus: 0,
              defensePenalty: 0,
            };
          })
          .filter(Boolean);

        if (!damageActions.length) {
          continue;
        }

        damageActions.sort(
          (a, b) => a.gagLevel - b.gagLevel || a.toonOrder - b.toonOrder
        );

        const luredCount = nextCogs.filter((cog) => cog.status === 'lured').length;
        const totalCogs = nextCogs.length;
        const hasLuredRatioMix =
          totalCogs > 0 && luredCount > 0 && luredCount < totalCogs;
        const luredRatioBonus = hasLuredRatioMix
          ? (luredCount / totalCogs) * 100
          : 0;
        const isLuredRatioEligible = (action) =>
          hasLuredRatioMix &&
          (trackName === 'Sound' ||
            (trackName === 'Throw' && action.gagInfo.gagName === 'Wedding Cake'));

        const applyStunBonusToActions = (actions) => {
          actions.forEach((action) => {
            action.stunBonus = getMaxStunBonus(action.targets);
            action.defensePenalty = getMaxDefense(action.targets);
          });
        };

        const buildComponentResultsForActions = (actions) => {
          if (!actions.length) {
            return { actionToResult: new Map() };
          }
          const actionTargets = actions.map((action) =>
            new Set(action.targets.map((cog) => cog.id))
          );
          const parents = actions.map((_, index) => index);
          const findRoot = (index) => {
            if (parents[index] !== index) {
              parents[index] = findRoot(parents[index]);
            }
            return parents[index];
          };
          const union = (a, b) => {
            const rootA = findRoot(a);
            const rootB = findRoot(b);
            if (rootA !== rootB) parents[rootB] = rootA;
          };
          for (let i = 0; i < actions.length; i += 1) {
            for (let j = i + 1; j < actions.length; j += 1) {
              const targetsA = actionTargets[i];
              const targetsB = actionTargets[j];
              let overlap = false;
              targetsA.forEach((id) => {
                if (targetsB.has(id)) overlap = true;
              });
              if (overlap) union(i, j);
            }
          }

          const componentMap = new Map();
          actions.forEach((action, index) => {
            const root = findRoot(index);
            const entry =
              componentMap.get(root) ?? {
                actions: [],
                targetIds: new Set(),
                rawAccuracy: 0,
                hasLuredTarget: false,
              };
            entry.actions.push(action);
            entry.rawAccuracy = Math.max(
              entry.rawAccuracy,
              (action.rawAccuracy ?? 0) +
                (action.stunBonus ?? 0) -
                (action.defensePenalty ?? 0)
            );
            action.targets.forEach((cog) => {
              entry.targetIds.add(cog.id);
              if (cog.status === 'lured') entry.hasLuredTarget = true;
            });
            componentMap.set(root, entry);
          });

          const componentResults = new Map();
          componentMap.forEach((entry, key) => {
            const hasLuredRatioBonus = entry.actions.some(isLuredRatioEligible);
            const guaranteedHit =
              entry.hasLuredTarget &&
              (trackName === 'Sound' ||
                trackName === 'Throw' ||
                trackName === 'Squirt');
            const guaranteedMiss =
              trackName === 'Drop' && entry.hasLuredTarget;
            const totalRawAccuracy =
              entry.rawAccuracy + (hasLuredRatioBonus ? luredRatioBonus : 0);
            const effectiveAccuracy =
              guaranteedMiss
                ? 0
                : hasLuredRatioBonus
                  ? clampAccuracyValue(totalRawAccuracy)
                  : guaranteedHit
                    ? 100
                    : clampAccuracyValue(entry.rawAccuracy);
            const hit = rollChance(effectiveAccuracy);
            componentResults.set(key, {
              effectiveAccuracy,
              rawAccuracy: entry.rawAccuracy,
              totalAccuracy: totalRawAccuracy,
              hit,
              shared: entry.actions.length > 1,
              guaranteed: guaranteedHit && !hasLuredRatioBonus,
              guaranteedMiss,
              luredRatioBonus: hasLuredRatioBonus ? luredRatioBonus : 0,
            });
          });

          const actionToResult = new Map();
          actions.forEach((action, index) => {
            const root = findRoot(index);
            actionToResult.set(action, componentResults.get(root));
          });
          return { actionToResult };
        };

        const hasWeddingCake =
          trackName === 'Throw' &&
          damageActions.some((action) => action.gagInfo.gagName === 'Wedding Cake');
        const cakeActions = hasWeddingCake
          ? damageActions.filter((action) => action.gagInfo.gagName === 'Wedding Cake')
          : [];
        const otherActions = hasWeddingCake
          ? damageActions.filter((action) => action.gagInfo.gagName !== 'Wedding Cake')
          : damageActions;
        applyStunBonusToActions(otherActions);
        const otherResults = buildComponentResultsForActions(otherActions);
        const actionResults = new Map(otherResults.actionToResult);

        const applyStunResults = (actions, resultMap) => {
          actions.forEach((action) => {
            const result = resultMap.get(action);
            action.targets.forEach((cog) => {
              if (!result?.hit) {
                resetStunBonus(cog.id);
                return;
              }
              applyStunHit(cog.id, trackName, 25);
            });
          });
        };

        applyStunResults(otherActions, otherResults.actionToResult);
        if (hasWeddingCake) {
          applyStunBonusToActions(cakeActions);
          const cakeResults = buildComponentResultsForActions(cakeActions);
          cakeActions.forEach((action) => {
            actionResults.set(action, cakeResults.actionToResult.get(action));
          });
          applyStunResults(cakeActions, cakeResults.actionToResult);
        }

        const getResultForAction = (action) => actionResults.get(action);
        const weddingCakeHit = hasWeddingCake
          ? !!cakeActions.find((action) => actionResults.get(action)?.hit)
          : false;
        const clearsLureOnHit =
          trackName === 'Sound' ||
          trackName === 'Squirt' ||
          (trackName === 'Throw' && (!hasWeddingCake || weddingCakeHit));

        const usageLines = [];
        const dodgeLines = [];
        const dodgeToasts = [];
        damageActions.forEach((action) => {
          const result = getResultForAction(action);
          const targetCog =
            action.targets.length === 1 ? action.targets[0] : null;
          const targetText =
            action.targets.length > 1
              ? ''
              : targetCog
                ? `on ${cogLabel(targetCog)}`
                : '';
          const sharedText = result?.shared ? 'shared roll, ' : '';
          const guaranteedText = result?.guaranteed
            ? 'guaranteed hit, '
            : result?.guaranteedMiss
              ? 'guaranteed miss, '
              : '';
          const accuracySummary = buildAccuracySummary(action.accuracyBreakdown, [
            { label: 'Stun bonus', value: action.stunBonus ?? 0 },
            { label: 'Defense', value: action.defensePenalty ? -action.defensePenalty : 0 },
            { label: 'Lured ratio bonus', value: result?.luredRatioBonus ?? 0 },
          ]);
          const actionTotal = accuracySummary?.total ?? 0;
          const sharedDetail =
            result?.shared && result?.totalAccuracy != null && actionTotal !== result.totalAccuracy
              ? ` (max ${Math.round(result.totalAccuracy)}% total)`
              : '';
          nextToons = nextToons.map((item) =>
            item.id === action.toon.id ? clearToonGagData(item) : item
          );
          usageLines.push(
            `${toonLabel(action.toon)} used ${action.gagInfo.gagName}${
              targetText ? ` ${targetText}` : ''
            }. ${result?.hit ? 'Hit' : 'Missed'} (${sharedText}${guaranteedText}${describeAccuracy(
              result?.effectiveAccuracy,
              result?.rawAccuracy,
              false
            )}${accuracySummary ? `; ${accuracySummary.text}` : ''}${sharedDetail}).`
          );
          if (!result?.hit) {
            action.targets.forEach((cog) => {
              dodgeLines.push(`${cogLabel(cog)} dodged.`);
              dodgeToasts.push(
                createToast('cog', cog.id, 'MISSED', 'miss')
              );
            });
          }
        });
        logLines(usageLines);
        if (dodgeLines.length) {
          logLines(dodgeLines, dodgeToasts);
        }

        const baseHitsByCog = new Map();
        damageActions.forEach((action) => {
          const result = getResultForAction(action);
          if (!result?.hit) return;
          action.targets.forEach((cog) => {
            const entries = baseHitsByCog.get(cog.id) ?? [];
            entries.push({
              damage: action.power,
              gagName: action.gagInfo.gagName,
            });
            baseHitsByCog.set(cog.id, entries);
          });
        });

        const pendingV2Promotions = new Set();

        damageActions.forEach((action) => {
          const result = getResultForAction(action);
          if (!result?.hit) return;
          const targetIds = new Set(action.targets.map((cog) => cog.id));
          const targetCogs = nextCogs.filter((cog) => targetIds.has(cog.id));
          if (!targetCogs.length) return;
          const actionLines = [];
          const actionToasts = [];
          targetCogs.forEach((cog) => {
            const currentHp = cog.currentHp ?? getMaxHp(cog.level);
            const rawNextHp = Math.max(currentHp - action.power, 0);
            const shouldPromote = rawNextHp <= 0 && cog.v2 && !cog.skelecog;
            const nextHp = shouldPromote ? 0 : rawNextHp;
            nextCogs = nextCogs.map((item) => {
              if (item.id !== cog.id) return item;
              return {
                ...item,
                currentHp: nextHp,
                v2: item.v2,
                skelecog: item.skelecog,
                status: item.status,
                lureRounds: item.lureRounds,
              };
            });
            actionLines.push(
              `${cogLabel(cog)} -${action.power} HP (${nextHp}/${getMaxHp(cog.level)}) (${action.gagInfo.gagName}).`
            );
            actionToasts.push(
              createToast('cog', cog.id, `-${action.power}`, 'damage')
            );
            if (shouldPromote) {
              pendingV2Promotions.add(cog.id);
            }
          });
          logLines(actionLines, actionToasts);
          if (pendingV2Promotions.size) {
            nextCogs = nextCogs.map((item) =>
              pendingV2Promotions.has(item.id)
                ? { ...item, currentHp: 0 }
                : item
            );
          }
        });

        const lureLines = [];
        const lureToasts = [];
        const comboLines = [];
        const comboToasts = [];
        const defeatLines = [];
        const clearLureLines = [];
        const defeatIds = new Set();
        const clearLureIds = new Set();

        nextCogs.forEach((cog) => {
          const baseHits = baseHitsByCog.get(cog.id);
          if (!baseHits?.length) return;
          const baseDamage = baseHits.reduce(
            (total, entry) => total + entry.damage,
            0
          );
          const isLured = cog.status === 'lured';
          const lureBonus =
            isLured && (trackName === 'Throw' || trackName === 'Squirt')
              ? Math.floor(baseDamage * 0.5)
              : 0;
          if (!lureBonus) return;
          const currentHp = cog.currentHp ?? getMaxHp(cog.level);
          const rawNextHp = Math.max(currentHp - lureBonus, 0);
          const shouldPromote = rawNextHp <= 0 && cog.v2 && !cog.skelecog;
          const nextHp = shouldPromote ? 0 : rawNextHp;
          nextCogs = nextCogs.map((item) =>
            item.id === cog.id ? { ...item, currentHp: nextHp } : item
          );
          lureLines.push(
            `${cogLabel(cog)} -${lureBonus} HP (${nextHp}/${getMaxHp(cog.level)}) (Lure knockback).`
          );
          lureToasts.push(
            createToast('cog', cog.id, `-${lureBonus}`, 'lure')
          );
          if (shouldPromote) {
            pendingV2Promotions.add(cog.id);
          }
        });

        logLines(lureLines, lureToasts);

        nextCogs.forEach((cog) => {
          const baseHits = baseHitsByCog.get(cog.id);
          if (!baseHits?.length) return;
          const baseDamage = baseHits.reduce(
            (total, entry) => total + entry.damage,
            0
          );
          const hitCount = baseHits.length;
          const sameTrackBonus =
            hitCount >= 2 ? Math.floor(baseDamage * 0.2) : 0;
          if (!sameTrackBonus) return;
          const currentHp =
            nextCogs.find((item) => item.id === cog.id)?.currentHp ??
            getMaxHp(cog.level);
          const rawNextHp = Math.max(currentHp - sameTrackBonus, 0);
          const shouldPromote = rawNextHp <= 0 && cog.v2 && !cog.skelecog;
          const nextHp = shouldPromote ? 0 : rawNextHp;
          nextCogs = nextCogs.map((item) =>
            item.id === cog.id ? { ...item, currentHp: nextHp } : item
          );
          comboLines.push(
            `${cogLabel(cog)} -${sameTrackBonus} HP (${nextHp}/${getMaxHp(cog.level)}) (Combo damage).`
          );
          comboToasts.push(
            createToast('cog', cog.id, `-${sameTrackBonus}`, 'combo')
          );
          if (shouldPromote) {
            pendingV2Promotions.add(cog.id);
          }
        });

        logLines(comboLines, comboToasts);
        if (pendingV2Promotions.size) {
          const promotionLines = [];
          nextCogs = nextCogs.map((item) => {
            if (!pendingV2Promotions.has(item.id)) return item;
            promotionLines.push(`${cogLabel(item)} v2.0 layer destroyed.`);
            return {
              ...item,
              currentHp: getMaxHp(item.level),
              v2: false,
              skelecog: true,
            };
          });
          logLines(promotionLines);
        }

        nextCogs.forEach((cog) => {
          const baseHits = baseHitsByCog.get(cog.id);
          if (!baseHits?.length) return;
          const isLured = cog.status === 'lured';
          const clearsLure = isLured && clearsLureOnHit;
          const finalHp =
            nextCogs.find((item) => item.id === cog.id)?.currentHp ?? 0;
          if (finalHp <= 0) {
            if (!(cog.v2 && !cog.skelecog)) {
              defeatIds.add(cog.id);
              defeatLines.push(`${cogLabel(cog)} was defeated!`);
            }
          } else if (clearsLure) {
            clearLureIds.add(cog.id);
          }
        });

        if (defeatIds.size) {
          nextCogs = nextCogs.filter((item) => !defeatIds.has(item.id));
          logLines(defeatLines);
        }

        if (clearLureIds.size) {
          nextCogs = nextCogs.map((item) =>
            clearLureIds.has(item.id)
              ? { ...item, status: null, lureRounds: '' }
              : item
          );
          clearLureIds.forEach((cogId) => {
            const cog = nextCogs.find((item) => item.id === cogId);
            if (cog) {
              clearLureLines.push(`${cogLabel(cog)} is no longer lured.`);
            }
          });
          logLines(clearLureLines);
        }

        continue;
      }

      if (trackName === 'Toon-Up') {
        const toonUpActions = actingToons
          .map((toon) => {
            const gagInfo = getGagInfo(toon.gag);
            if (!gagInfo || !toon.target) return null;
            const accuracyBreakdown = getAccuracyBreakdown(toon, gagInfo);
            const rawAccuracy = accuracyBreakdown?.total ?? 0;
            const chance = clampAccuracyValue(rawAccuracy);
            const power = getGagPower(toon, gagInfo);
            if (power == null) return null;
            const isHit = rollChance(chance);
            const totalHeal = isHit ? power : Math.ceil(power * 0.2);
            const targets =
              toon.target?.type === 'all-toons-except-self'
                ? nextToons.filter((item) => item.id !== toon.id)
                : toon.target?.type === 'toon'
                  ? nextToons.filter((item) => item.id === toon.target?.id)
                  : [];
            const targetText =
              toon.target?.type === 'all-toons-except-self'
                ? 'on all other toons'
                : targets[0]
                  ? `on ${toonLabel(targets[0])}`
                  : 'on a toon';
            const healPerTarget =
              toon.target?.type === 'all-toons-except-self'
                ? Math.floor(totalHeal / targets.length)
                : totalHeal;
            return {
              toon,
              gagInfo,
              chance,
              accuracyBreakdown,
              targets,
              targetText,
              healPerTarget,
              isHit,
            };
          })
          .filter(Boolean);

        if (toonUpActions.length) {
          const usageLines = [];
          toonUpActions.forEach((action) => {
            const actor = toonLabel(action.toon);
            const accuracySummary = buildAccuracySummary(action.accuracyBreakdown);
            nextToons = nextToons.map((item) =>
              item.id === action.toon.id ? clearToonGagData(item) : item
            );
            usageLines.push(
              `${actor} used ${action.gagInfo.gagName} ${action.targetText}. ${
                action.isHit ? 'Hit' : 'Missed'
              } (${describeAccuracy(action.chance, null, false)}${
                accuracySummary ? `; ${accuracySummary.text}` : ''
              }).`
            );
          });
          logLines(usageLines);

          toonUpActions.forEach((action) => {
            if (!action.targets.length || !action.healPerTarget) {
              logLines(['No toon was healed.']);
              return;
            }
            const healLines = [];
            const healToasts = [];
            action.targets.forEach((target) => {
              const nextLaff = Math.min(
                target.maxLaff,
                (target.currentLaff ?? 0) + action.healPerTarget
              );
              nextToons = nextToons.map((item) =>
                item.id === target.id
                  ? { ...item, currentLaff: nextLaff }
                  : item
              );
              healLines.push(
                `${toonLabel(target)} +${action.healPerTarget} laff (${nextLaff}/${target.maxLaff}).`
              );
              healToasts.push(
                createToast('toon', target.id, `+${action.healPerTarget}`, 'heal')
              );
            });
            logLines(healLines, healToasts);
          });
        }
        continue;
      }
      if (!nextCogs.length) break;
    }

    const cogChance = getCogAccuracyChance(cogAccuracyConfig);
    for (let i = nextCogs.length - 1; i >= 0; i -= 1) {
      const cog = nextCogs[i];
      if (!nextToons.length) break;
      if (cog.turnResolved) continue;
      if (cog.fresh) {
        markCogResolved(cog.id);
        continue;
      }
      if (cog.status === 'lured') {
        const roundsValue = Number.parseInt(cog.lureRounds, 10);
        const remainingRounds = Number.isNaN(roundsValue) ? 0 : roundsValue;
        const nextRounds = Math.max(remainingRounds - 1, 0);
        const label = cogLabel(cog);
        if (nextRounds > 0) {
          nextCogs = nextCogs.map((item) =>
            item.id === cog.id
              ? { ...item, lureRounds: String(nextRounds) }
              : item
          );
          markCogResolved(cog.id);
          logLines(
            [
              `${label} is lured and skips its attack (${nextRounds} rounds remaining).`,
            ],
            [
              createToast('cog', cog.id, `x${nextRounds}`, 'status', {
                icon: luredIcon,
              }),
            ]
          );
          continue;
        }
        nextCogs = nextCogs.map((item) =>
          item.id === cog.id
            ? { ...item, status: null, lureRounds: '' }
            : item
        );
        markCogResolved(cog.id);
        logLine(`${label} is no longer lured and attacks.`);
      }
      const attacks = getCogAttacks(cog.value, cog.level);
      if (!attacks) {
        markCogResolved(cog.id);
        continue;
      }
      const attackName = pickCogAttack(attacks, nextToons, cogUsageConfig, null, null);
      if (!attackName) {
        markCogResolved(cog.id);
        continue;
      }
      const attack = attacks[attackName];
      const attackLabel = humanizeCamelCase(attackName);
      markCogResolved(cog.id);
      logLine(`${cogLabel(cog)} used ${attackLabel} (${describeAccuracy(cogChance)}).`);
      const attackToast = createToast('cog', cog.id, attackLabel, 'attack');
      if (attack?.affects === 'multi') {
        const resultLines = [];
        const resultToasts = [];
        const defeatedIds = new Set();
        const defeatedLines = [];
        let toonIndex = 0;
        while (toonIndex < nextToons.length) {
          const toon = nextToons[toonIndex];
          const label = toonLabel(toon);
          const hit = rollChance(cogChance);
          if (!hit) {
            resultLines.push(`${label} dodged (${describeAccuracy(cogChance)}).`);
            resultToasts.push(createToast('toon', toon.id, 'MISSED', 'miss'));
            toonIndex += 1;
            continue;
          }
          const currentLaff = toon.currentLaff ?? 0;
          const nextLaff = Math.max(currentLaff - (attack.damage ?? 0), 0);
          nextToons = nextToons.map((item) =>
            item.id === toon.id ? { ...item, currentLaff: nextLaff } : item
          );
          resultLines.push(
            `${label} -${attack.damage ?? 0} laff (${nextLaff}/${toon.maxLaff}).`
          );
          resultToasts.push(
            createToast('toon', toon.id, `-${attack.damage ?? 0}`, 'damage')
          );
          if (nextLaff <= 0) {
            defeatedIds.add(toon.id);
            defeatedLines.push(`${label} was defeated!`);
          }
          toonIndex += 1;
        }
        logLines(resultLines, [attackToast, ...resultToasts]);
        if (defeatedIds.size) {
          nextToons = nextToons.filter((item) => !defeatedIds.has(item.id));
          logLines(defeatedLines);
        }
      } else {
        const targetId = pickCogTargetForSimulation(nextToons, cogTargetConfig);
        const targetToon = nextToons.find((toon) => toon.id === targetId);
        if (!targetToon) continue;
        const hit = rollChance(cogChance);
        if (!hit) {
          logLine(
            `${toonLabel(targetToon)} dodged (${describeAccuracy(cogChance)}).`,
            [attackToast, createToast('toon', targetToon.id, 'MISSED', 'miss')]
          );
        } else {
          const currentLaff = targetToon.currentLaff ?? 0;
          const nextLaff = Math.max(currentLaff - (attack.damage ?? 0), 0);
          const targetLabel = toonLabel(targetToon);
          nextToons = nextToons.map((toon) =>
            toon.id === targetToon.id ? { ...toon, currentLaff: nextLaff } : toon
          );
          logLine(
            `${targetLabel} -${attack.damage ?? 0} laff (${nextLaff}/${targetToon.maxLaff}).`,
            [
              attackToast,
              createToast('toon', targetToon.id, `-${attack.damage ?? 0}`, 'damage'),
            ]
          );
          if (nextLaff <= 0) {
            nextToons = nextToons.filter((toon) => toon.id !== targetToon.id);
            logLine(`${targetLabel} was defeated!`);
          }
        }
      }
      if (!nextToons.length) break;
    }

    if (
      battleMode &&
      nextToons.length &&
      (battleCogEndless || nextBattlePool > 0) &&
      nextCogs.length < COG_BATTLE_SLOTS
    ) {
      const slotsAvailable = COG_BATTLE_SLOTS - nextCogs.length;
      const toAdd = battleCogEndless
        ? slotsAvailable
        : Math.min(slotsAvailable, nextBattlePool);
      const baseCogs = nextCogs;
      const spawnedCogs = [];
      logLine('Cogs are joining the battle.');
      for (let i = 0; i < toAdd; i += 1) {
        const newCog = buildBattleCog({ fresh: true, battleType: cogBattleType });
        spawnedCogs.push(newCog);
        nextCogs = [...spawnedCogs, ...baseCogs];
        if (!battleCogEndless) {
          nextBattlePool -= 1;
        }
        logLine(
          `${getCogLabel(newCog.value)}, level ${newCog.level} has joined the battle!`
        );
      }
    }

    if (!nextCogs.length && nextToons.length) {
      logLine('All cogs have been defeated.');
    }

    if (!nextToons.length) {
      logLine('All toons have been defeated.');
    }
    nextToons = nextToons.map((toon) => ({
      ...clearToonGagData(toon),
    }));
    nextCogs = nextCogs.map((cog) => ({
      ...cog,
      stunBonus: 0,
      lastDamagedByTrack: null,
      fresh: false,
    }));
    logLine('Turn end');
    return {
      log,
      steps,
      nextCogs,
      nextToons,
    };
  };

  const isUsageLine = (line) =>
    /^(Toon|Cog) #\d+ .* used /.test(line ?? '');
  const isPassLine = (line) => /^Toon #\d+ .* passed\.$/.test(line ?? '');
  const splitUsageLine = (line) => {
    const separator = '. ';
    const splitIndex = line.indexOf(separator);
    if (splitIndex !== -1) {
      const after = line.slice(splitIndex + separator.length);
      if (after.startsWith('Hit') || after.startsWith('Missed')) {
        return {
          moveText: line.slice(0, splitIndex + 1),
          actionText: after,
        };
      }
    }
    return { moveText: line, actionText: null };
  };
  const buildMoveText = (usageLines, passLines) => {
    if (usageLines.length) {
      const segments = usageLines.map((line) => {
        const { moveText } = splitUsageLine(line);
        return moveText.endsWith('.') ? moveText.slice(0, -1) : moveText;
      });
      return `${segments.join(' and ')}.`;
    }
    return passLines.length > 1 ? 'Toons passed.' : 'Toon passed.';
  };
    const buildMovePlanFromSteps = (steps) => {
      const moves = [];
      let currentMove = null;
      let prevLogLength = 0;
      const joinMoveLine = 'Cogs are joining the battle.';
      const isStandaloneMoveLine = (line) =>
        line === 'All cogs have been defeated.' ||
        / is lured and skips its attack /.test(line ?? '');
      steps.forEach((step, stepIndex) => {
        const newLines = step.battleLog.slice(prevLogLength);
        prevLogLength = step.battleLog.length;
        const hasTurnEnd = newLines.includes('Turn end');
        const filtered = newLines.filter(
          (line) => line !== 'Turn start' && line !== 'Turn end'
        );
        if (!filtered.length) {
          if (hasTurnEnd && currentMove) {
            currentMove.actions.push({ stepIndex, lines: [] });
          }
          return;
        }
        if (filtered.includes(joinMoveLine)) {
          const actionLines = filtered.filter((line) => line !== joinMoveLine);
          currentMove = {
            text: joinMoveLine,
            actions: [],
          };
          moves.push(currentMove);
          if (actionLines.length) {
            currentMove.actions.push({ stepIndex, lines: actionLines });
          }
          return;
        }
        if (filtered.every(isStandaloneMoveLine)) {
          filtered.forEach((line) => {
            currentMove = {
              text: line,
            actions: [{ stepIndex, lines: [] }],
          };
          moves.push(currentMove);
        });
        return;
      }
      const usageLines = filtered.filter((line) => isUsageLine(line));
      const passLines = filtered.filter((line) => isPassLine(line));
      const otherLines = filtered.filter(
        (line) =>
          !isUsageLine(line) &&
          !isPassLine(line) &&
          !isStandaloneMoveLine(line)
      );
      if (usageLines.length || passLines.length) {
        currentMove = {
          text: buildMoveText(usageLines, passLines),
          actions: [],
        };
        moves.push(currentMove);
        const actionLines = [];
        if (!usageLines.length && passLines.length) {
          actionLines.push(...passLines);
        }
        usageLines.forEach((line) => {
          const { actionText } = splitUsageLine(line);
          if (actionText) actionLines.push(actionText);
        });
        actionLines.push(...otherLines);
        if (actionLines.length) {
          currentMove.actions.push({ stepIndex, lines: actionLines });
        }
        return;
      }
      if (currentMove) {
        currentMove.actions.push({ stepIndex, lines: filtered });
      }
    });
    return moves;
  };
  const buildFullLogFromPlan = (baseLog, moves, includeTurnEnd = true) => {
    const entries = [...baseLog];
    const hasTurnStart = entries.some(
      (entry) =>
        entry &&
        typeof entry !== 'string' &&
        entry.type === 'system' &&
        entry.text === 'Turn start'
    );
    if (!hasTurnStart) {
      entries.push({ type: 'system', text: 'Turn start' });
    }
    moves.forEach((move) => {
      entries.push({
        type: 'move',
        text: move.text,
        actions: move.actions
          .filter((action) => action.lines?.length)
          .map((action) => ({ lines: action.lines })),
      });
    });
    if (includeTurnEnd) {
      entries.push({ type: 'system', text: 'Turn end' });
    }
    return entries;
  };
  const appendMoveLog = (text) => {
    setBattleLog((prev) => [
      ...prev,
      { type: 'move', text, actions: [] },
    ]);
  };
  const appendActionLinesToLog = (prevLog, lines) => {
    const lineList = Array.isArray(lines) ? lines : [lines];
    if (!lineList.length) return prevLog;
    const next = [...prevLog];
    const lastIndex = next.length - 1;
    const last = next[lastIndex];
    if (!last || last.type !== 'move') {
      return next;
    }
    const actions = [...(last.actions ?? []), { lines: lineList }];
    next[lastIndex] = { ...last, actions };
    return next;
  };
  const appendActionLog = (lines) => {
    setBattleLog((prev) => appendActionLinesToLog(prev, lines));
  };
  const pushTurnHistorySnapshot = (snapshot) => {
    const nextIndex = turnHistoryIndexRef.current + 1;
    setTurnHistory((prev) => [...prev.slice(0, nextIndex), snapshot]);
    setTurnHistoryIndex(nextIndex);
    turnLogBaseRef.current = snapshot.battleLog;
  };
  const applyActionStep = (stepIndex, lines, showToasts = true) => {
    const step = turnSteps[stepIndex];
    if (!step) return;
    lastAppliedStepRef.current = stepIndex;
    toastTimersRef.current.forEach((timer) => clearTimeout(timer));
    toastTimersRef.current.clear();
    if (showToasts) {
      const nextToasts = (step.toasts ?? []).map((toast) =>
        toast?.id
          ? toast
          : { ...toast, id: toastIdRef.current++ }
      );
      setActionToasts(nextToasts);
      nextToasts.forEach((toast) => {
        const targetKey = `${toast.targetType}-${toast.targetId}`;
        const timer = setTimeout(() => {
          setActionToasts((prev) =>
            prev.filter((item) => item.id !== toast.id)
          );
          toastTimersRef.current.delete(targetKey);
        }, 1400);
        toastTimersRef.current.set(targetKey, timer);
      });
    } else {
      setActionToasts([]);
    }
    if (lines?.length) {
      appendActionLog(lines);
    }
    setCogs(step.cogs.map(cloneCog));
    setToons(step.toons.map(cloneToon));
    if (step.battleCogPoolRemaining != null) {
      setBattleCogPoolRemaining(step.battleCogPoolRemaining);
    }
  };
  const completeMove = (stepState) => {
    const remainingMoves = turnMoves.slice(1);
    const snapshot = {
      cogs: (stepState?.cogs ?? cogs).map(cloneCog),
      toons: (stepState?.toons ?? toons).map(cloneToon),
      battleLog: [...battleLogRef.current],
      battleCogPoolRemaining:
        stepState?.battleCogPoolRemaining ?? battleCogPoolRemaining,
    };
    pushTurnHistorySnapshot(snapshot);
    setIsMoveResolving(false);
    setCurrentActionIndex(-1);
    setCurrentMoveIndex(-1);
    setTurnMoves(remainingMoves);
    if (!remainingMoves.length) {
      setIsTurnReadyToEnd(true);
    }
  };
  const finalizeTurn = (appendTurnEnd = true) => {
    setTurnInProgress(false);
    setIsMoveResolving(false);
    setCurrentActionIndex(-1);
    setCurrentMoveIndex(-1);
    lastAppliedStepRef.current = null;
    setTurnSteps([]);
    setTurnMoves([]);
    setTurnHistory([]);
    setTurnHistoryIndex(-1);
    lastAppliedStepRef.current = null;
    turnLogBaseRef.current = [];
    setIsTurnReadyToEnd(false);
    if (moveTimerRef.current) {
      clearTimeout(moveTimerRef.current);
      moveTimerRef.current = null;
    }
    if (actionTimerRef.current) {
      clearTimeout(actionTimerRef.current);
      actionTimerRef.current = null;
    }
    if (appendTurnEnd) {
      setBattleLog((prev) => [...prev, { type: 'system', text: 'Turn end' }]);
    }
  };
  const startNextMove = () => {
    if (!turnMoves.length || isMoveResolving || isTurnReadyToEnd) return;
    const nextMove = turnMoves[0];
    appendMoveLog(nextMove.text);
    lastAppliedStepRef.current = null;
    setCurrentMoveIndex(0);
    setCurrentActionIndex(-1);
    setIsMoveResolving(true);
  };
  const advanceMoveAction = () => {
    const move = turnMoves[0];
    if (!move) {
      setIsTurnReadyToEnd(true);
      return;
    }
    const nextIndex = currentActionIndex + 1;
    const action = move.actions[nextIndex];
    if (!action) {
      const lastStepIndex = lastAppliedStepRef.current;
      const stepState =
        move.actions.length && lastStepIndex != null
          ? turnSteps[lastStepIndex]
          : null;
      completeMove(stepState);
      return;
    }
    applyActionStep(action.stepIndex, action.lines, true);
    setCurrentActionIndex(nextIndex);
  };
  const handleStartTurn = () => {
    if (!allToonsReady || !cogs.length) return;
    if (battleLog.length) {
      const entries = battleLog.map((entry) =>
        typeof entry === 'string'
          ? entry
          : {
              ...entry,
              actions: entry.actions?.map((action) => ({
                ...action,
                lines: Array.isArray(action.lines)
                  ? [...action.lines]
                  : action.lines,
              })),
            }
      );
      setBattleLogHistory((prev) => [
        ...prev,
        { label: `Turn #${prev.length + 1}`, entries },
      ]);
    }
    setLogViewIndex(null);
    setLastTurnSnapshot({
      cogs: cogs.map(cloneCog),
      toons: toons.map(cloneToon),
      battleLog: [...battleLog],
      battleCogPoolRemaining,
    });
    const { steps } = runTurnSimulation({
      recordSteps: true,
      startingBattlePool: battleCogPoolRemaining,
      battleMode: isBattleCogMode,
    });
    if (!steps.length) return;
    const startLog = [{ type: 'system', text: 'Turn start' }];
    turnLogBaseRef.current = startLog;
    setBattleLog(startLog);
    const moves = buildMovePlanFromSteps(steps);
    setTurnSteps(steps);
    setTurnMoves(moves);
    setTurnHistory([
      {
        cogs: cogs.map(cloneCog),
        toons: toons.map(cloneToon),
        battleLog: startLog,
        battleCogPoolRemaining,
      },
    ]);
    setTurnHistoryIndex(0);
    setCurrentMoveIndex(-1);
    setCurrentActionIndex(-1);
    setTurnInProgress(true);
    setIsMoveResolving(false);
    lastAppliedStepRef.current = null;
    setIsTurnReadyToEnd(false);
  };
  const handleFinishTurn = () => {
    if (!turnInProgress || !turnMoves.length) return;
    const finalStep = turnSteps[turnSteps.length - 1];
    if (finalStep) {
      setCogs(finalStep.cogs.map(cloneCog));
      setToons(finalStep.toons.map(cloneToon));
      if (finalStep.battleCogPoolRemaining != null) {
        setBattleCogPoolRemaining(finalStep.battleCogPoolRemaining);
      }
    }
    setActionToasts([]);
    toastTimersRef.current.forEach((timer) => clearTimeout(timer));
    toastTimersRef.current.clear();
    const fullLog = buildFullLogFromPlan(
      turnLogBaseRef.current,
      turnMoves,
      false
    );
    setBattleLog(fullLog);
    battleLogRef.current = fullLog;
    const snapshot = {
      cogs: (finalStep?.cogs ?? cogs).map(cloneCog),
      toons: (finalStep?.toons ?? toons).map(cloneToon),
      battleLog: fullLog,
      battleCogPoolRemaining:
        finalStep?.battleCogPoolRemaining ?? battleCogPoolRemaining,
    };
    pushTurnHistorySnapshot(snapshot);
    setTurnMoves([]);
    setIsTurnReadyToEnd(true);
  };
  const handleEndTurn = () => {
    if (!turnInProgress || !isTurnReadyToEnd) return;
    finalizeTurn(true);
  };
  const handleClearLogHistory = () => {
    if (!battleLogHistory.length) return;
    const shouldClear = window.confirm(
      'Are you sure you want to clear all your saved turns?'
    );
    if (!shouldClear) return;
    setBattleLogHistory([]);
    setLogViewIndex(null);
  };
  const handleManualStep = () => {
    if (autoAdvance) {
      setAutoAdvance(false);
    }
    if (isMoveResolving) {
      if (actionTimerRef.current) {
        clearTimeout(actionTimerRef.current);
        actionTimerRef.current = null;
      }
      const move = turnMoves[0];
      if (!move) return;
      const remainingActions = move.actions.slice(currentActionIndex + 1);
      if (!remainingActions.length) {
        completeMove(null);
        return;
      }
      let nextLog = battleLogRef.current;
      let lastStepIndex = null;
      let lastToastStep = null;
      remainingActions.forEach((action) => {
        if (action.lines?.length) {
          nextLog = appendActionLinesToLog(nextLog, action.lines);
        }
        lastStepIndex = action.stepIndex;
        const step = turnSteps[action.stepIndex];
        if (step?.toasts?.length) {
          lastToastStep = step;
        }
      });
      if (nextLog !== battleLogRef.current) {
        setBattleLog(nextLog);
        battleLogRef.current = nextLog;
      }
      if (lastStepIndex != null) {
        lastAppliedStepRef.current = lastStepIndex;
        const stepState = turnSteps[lastStepIndex];
        if (stepState) {
          setCogs(stepState.cogs.map(cloneCog));
          setToons(stepState.toons.map(cloneToon));
          if (stepState.battleCogPoolRemaining != null) {
            setBattleCogPoolRemaining(stepState.battleCogPoolRemaining);
          }
        }
      }
      toastTimersRef.current.forEach((timer) => clearTimeout(timer));
      toastTimersRef.current.clear();
      if (lastToastStep) {
        const nextToasts = (lastToastStep.toasts ?? []).map((toast) =>
          toast?.id
            ? toast
            : { ...toast, id: toastIdRef.current++ }
        );
        setActionToasts(nextToasts);
        nextToasts.forEach((toast) => {
          const targetKey = `${toast.targetType}-${toast.targetId}`;
          const timer = setTimeout(() => {
            setActionToasts((prev) =>
              prev.filter((item) => item.id !== toast.id)
            );
            toastTimersRef.current.delete(targetKey);
          }, 1400);
          toastTimersRef.current.set(targetKey, timer);
        });
      } else {
        setActionToasts([]);
      }
      completeMove(lastStepIndex != null ? turnSteps[lastStepIndex] : null);
      return;
    }
    startNextMove();
  };
  const handleStepBack = () => {
    if (!turnInProgress) return;
    if (autoAdvance) {
      setAutoAdvance(false);
    }
    setIsTurnReadyToEnd(false);
    const isMidMove = isMoveResolving;
    const targetIndex = isMidMove ? turnHistoryIndex : turnHistoryIndex - 1;
    if (targetIndex < 0) return;
    if (moveTimerRef.current) {
      clearTimeout(moveTimerRef.current);
      moveTimerRef.current = null;
    }
    if (actionTimerRef.current) {
      clearTimeout(actionTimerRef.current);
      actionTimerRef.current = null;
    }
    toastTimersRef.current.forEach((timer) => clearTimeout(timer));
    toastTimersRef.current.clear();
    setActionToasts([]);
    const snapshot = turnHistory[targetIndex];
    if (!snapshot) return;
    setBattleLog([...snapshot.battleLog]);
    setCogs(snapshot.cogs.map(cloneCog));
    setToons(snapshot.toons.map(cloneToon));
    if (snapshot.battleCogPoolRemaining != null) {
      setBattleCogPoolRemaining(snapshot.battleCogPoolRemaining);
    }
    const { steps } = runTurnSimulation({
      recordSteps: true,
      startingCogs: snapshot.cogs,
      startingToons: snapshot.toons,
      startingBattlePool: snapshot.battleCogPoolRemaining ?? 0,
      battleMode: isBattleCogMode,
      skipTurnStart: true,
      preserveResolved: true,
    });
    const moves = buildMovePlanFromSteps(steps);
    setTurnSteps(steps);
    setTurnMoves(moves);
    setIsTurnReadyToEnd(moves.length === 0);
    setIsMoveResolving(false);
    setCurrentActionIndex(-1);
    setCurrentMoveIndex(-1);
    setTurnHistory((prev) => prev.slice(0, targetIndex + 1));
    setTurnHistoryIndex(targetIndex);
    turnLogBaseRef.current = [...snapshot.battleLog];
  };
  const handleUndoTurn = () => {
    if (!lastTurnSnapshot) return;
    setCogs(lastTurnSnapshot.cogs.map(cloneCog));
    setToons(lastTurnSnapshot.toons.map(cloneToon));
    setBattleLog([...lastTurnSnapshot.battleLog]);
    if (lastTurnSnapshot.battleCogPoolRemaining != null) {
      setBattleCogPoolRemaining(lastTurnSnapshot.battleCogPoolRemaining);
    }
    setLastTurnSnapshot(null);
    turnLogBaseRef.current = [];
    setTurnSteps([]);
    setTurnMoves([]);
    setTurnHistory([]);
    setTurnHistoryIndex(-1);
    setCurrentMoveIndex(-1);
    setCurrentActionIndex(-1);
    setTurnInProgress(false);
    setIsMoveResolving(false);
    lastAppliedStepRef.current = null;
    setIsTurnReadyToEnd(false);
    if (moveTimerRef.current) {
      clearTimeout(moveTimerRef.current);
      moveTimerRef.current = null;
    }
    if (actionTimerRef.current) {
      clearTimeout(actionTimerRef.current);
      actionTimerRef.current = null;
    }
    toastTimersRef.current.forEach((timer) => clearTimeout(timer));
    toastTimersRef.current.clear();
    setActionToasts([]);
  };


  useEffect(() => {
    setToons((prev) => {
      const result = prev.reduce(
        (acc, toon) => {
          let nextToon = toon;
          if (toon.target?.type?.includes('cog')) {
            const hasValidCog =
              toon.target?.type === 'all-cogs'
                ? cogs.length > 0
                : toon.target?.type === 'cog'
                  ? cogs.some((cog) => cog.id === toon.target?.id)
                  : false;
            if (!hasValidCog) {
              nextToon = { ...nextToon, target: null };
            }
          }
          if (!nextToon.target) {
            const autoTarget = getAutoTarget(nextToon, prev, cogs);
            if (autoTarget?.type?.includes('cog')) {
              nextToon = { ...nextToon, target: autoTarget };
            }
          }
          acc.changed = acc.changed || nextToon !== toon;
          acc.toons.push(nextToon);
          return acc;
        },
        { changed: false, toons: [] }
      );
      return result.changed ? result.toons : prev;
    });
  }, [cogs.length]);

  useEffect(() => {
    const anyTrapped = cogs.some((cog) => cog.status === 'trapped');
    const allLured = cogs.length > 0 && cogs.every((cog) => cog.status === 'lured');
    const validTrapTargets = cogs.filter((cog) => !cog.status);
    const validTrapIds = new Set(validTrapTargets.map((cog) => cog.id));
    const autoTrapId =
      validTrapTargets.length === 1 ? validTrapTargets[0].id : null;
    setToons((prev) => {
      let changed = false;
      const next = prev.map((toon) => {
        const gagInfo = getGagInfo(toon.gag);
        if (gagInfo?.trackName !== 'Trap') return toon;
        let nextToon = toon;
        if (gagInfo.targeting === 'all-cogs') {
          const nextTarget =
            !anyTrapped && !allLured && cogs.length ? { type: 'all-cogs' } : null;
          if (
            (toon.target?.type === 'all-cogs' && !nextTarget) ||
            (toon.target?.type !== 'all-cogs' && nextTarget)
          ) {
            nextToon = { ...nextToon, target: nextTarget };
          }
          if (toon.target?.type === 'cog') {
            nextToon = { ...nextToon, target: nextTarget };
          }
        } else {
          if (toon.target?.type === 'cog' && !validTrapIds.has(toon.target.id)) {
            nextToon = { ...nextToon, target: null };
          }
          if (!nextToon.target && autoTrapId) {
            nextToon = { ...nextToon, target: { type: 'cog', id: autoTrapId } };
          }
        }
        if (nextToon !== toon) changed = true;
        return nextToon;
      });
      return changed ? next : prev;
    });
  }, [cogs.map((cog) => `${cog.id}:${cog.status}`).join('|')]);

  useEffect(() => {
    const validLureTargets = cogs.filter((cog) => cog.status !== 'lured');
    const validLureIds = new Set(validLureTargets.map((cog) => cog.id));
    const autoLureId =
      validLureTargets.length === 1 ? validLureTargets[0].id : null;
    setToons((prev) => {
      let changed = false;
      const next = prev.map((toon) => {
        const gagInfo = getGagInfo(toon.gag);
        if (gagInfo?.trackName !== 'Lure') return toon;
        let nextToon = toon;
        if (gagInfo.targeting === 'all-cogs') {
          const nextTarget =
            validLureTargets.length && cogs.length ? { type: 'all-cogs' } : null;
          if (
            (toon.target?.type === 'all-cogs' && !nextTarget) ||
            (toon.target?.type !== 'all-cogs' && nextTarget)
          ) {
            nextToon = { ...nextToon, target: nextTarget };
          }
          if (toon.target?.type === 'cog') {
            nextToon = { ...nextToon, target: nextTarget };
          }
        } else {
          if (toon.target?.type === 'cog' && !validLureIds.has(toon.target.id)) {
            nextToon = { ...nextToon, target: null };
          }
          if (!nextToon.target && autoLureId) {
            nextToon = { ...nextToon, target: { type: 'cog', id: autoLureId } };
          }
        }
        if (nextToon !== toon) changed = true;
        return nextToon;
      });
      return changed ? next : prev;
    });
  }, [cogs.map((cog) => `${cog.id}:${cog.status}`).join('|')]);

  useEffect(() => {
    setToons((prev) => {
      const result = prev.reduce(
        (acc, toon) => {
          let nextToon = toon;
          if (toon.target?.type?.includes('toon')) {
            nextToon = { ...nextToon, target: null };
          }
          if (!nextToon.target) {
            const autoTarget = getAutoTarget(nextToon, prev, cogs);
            if (autoTarget?.type?.includes('toon')) {
              nextToon = { ...nextToon, target: autoTarget };
            }
          }
          acc.changed = acc.changed || nextToon !== toon;
          acc.toons.push(nextToon);
          return acc;
        },
        { changed: false, toons: [] }
      );
      return result.changed ? result.toons : prev;
    });
  }, [toons.length]);

  useEffect(() => {
    if (toons.length >= 2) return;
    setToons((prev) =>
      prev.map((toon) => {
        const gagInfo = getGagInfo(toon.gag);
        if (gagInfo?.trackName !== 'Toon-Up') return toon;
        return {
          ...toon,
          gag: null,
          target: null,
          unmaxed: false,
          highestGagLevel: null,
          gagPower: null,
          organic: false,
          accuracyOverride: false,
          accuracyOverrideBase: 100,
          accuracyOverrideTrack: 0,
        };
      })
    );
  }, [toons.length]);

  useEffect(() => {
    activeScreenRef.current = activeScreen;
  }, [activeScreen]);

  useEffect(() => {
    autoAdvanceRef.current = autoAdvance;
  }, [autoAdvance]);

  useEffect(() => {
    battleLogRef.current = battleLog;
  }, [battleLog]);

  useEffect(() => {
    turnHistoryIndexRef.current = turnHistoryIndex;
  }, [turnHistoryIndex]);

  useEffect(() => {
    if (!logBodyRef.current) return;
    logBodyRef.current.scrollTop = logBodyRef.current.scrollHeight;
  }, [displayedLog, logViewIndex]);

  useEffect(() => {
    if (!isMoveResolving) return;
    if (actionTimerRef.current) {
      clearTimeout(actionTimerRef.current);
    }
    actionTimerRef.current = setTimeout(() => {
      advanceMoveAction();
    }, 1000);
    return () => {
      if (actionTimerRef.current) {
        clearTimeout(actionTimerRef.current);
      }
    };
  }, [isMoveResolving, currentActionIndex, currentMoveIndex]);

  useEffect(() => {
    if (!turnInProgress || !autoAdvance || isMoveResolving || isTurnReadyToEnd) return;
    if (!turnMoves.length) return;
    if (moveTimerRef.current) {
      clearTimeout(moveTimerRef.current);
    }
    moveTimerRef.current = setTimeout(() => {
      if (!autoAdvanceRef.current) return;
      startNextMove();
    }, 1000);
    return () => {
      if (moveTimerRef.current) {
        clearTimeout(moveTimerRef.current);
      }
    };
  }, [autoAdvance, turnInProgress, isMoveResolving, turnMoves.length]);

  useEffect(() => {
    const y = scrollPositionsRef.current[activeScreen] ?? 0;
    window.scrollTo({ top: y, behavior: 'auto' });
  }, [activeScreen]);

  const handleScreenChange = (nextScreen) => {
    const current = activeScreenRef.current;
    scrollPositionsRef.current[current] = window.scrollY;
    setActiveScreen(nextScreen);
  };

  const isBattleScreen = activeScreen === 'battle';
  const isCogsScreen = activeScreen === 'cogs';
  const isToonsScreen = activeScreen === 'toons';
  const canStartTurn = allToonsReady && cogs.length > 0 && !turnInProgress;
  const canAdvanceStep =
    turnInProgress && turnMoves.length > 0 && !isTurnReadyToEnd;
  const canFastForward =
    turnInProgress && turnMoves.length > 0 && !isTurnReadyToEnd;
  const canStepBack =
    turnInProgress && (turnHistoryIndex > 0 || isMoveResolving);
  const renderBattleLogText = (text, entryIndex, lineIndex = 0) => {
    if (typeof text !== 'string') return text;
    const regex = /(Cog|Toon) #(\d+) \([^)]+\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      const isCog = match[1] === 'Cog';
      const className = isCog
        ? 'battle-log__cog'
        : `battle-log__toon battle-log__toon--${match[2]}`;
      parts.push(
        <span
          key={`log-${entryIndex}-${lineIndex}-${match.index}`}
          className={className}
        >
          {match[0]}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    return parts.length ? parts : text;
  };

  const renderBattleLogEntry = (entry, entryIndex) => {
    if (!entry || typeof entry === 'string') {
      return renderBattleLogText(entry ?? '', entryIndex);
    }
    if (entry.type === 'system') {
      return (
        <p className="battle-log__move-title">
          {renderBattleLogText(entry.text ?? '', entryIndex)}
        </p>
      );
    }
    if (entry.type === 'move') {
      return (
        <div className="battle-log__move">
          <p className="battle-log__move-title">
            {renderBattleLogText(entry.text ?? '', entryIndex)}
          </p>
          {entry.actions?.length ? (
            <ul className="battle-log__actions">
              {entry.actions.map((action, actionIndex) => (
                <li key={`action-${entryIndex}-${actionIndex}`}>
                  {Array.isArray(action.lines)
                    ? action.lines.map((line, lineIndex) => (
                        <div key={`line-${entryIndex}-${actionIndex}-${lineIndex}`}>
                          {renderBattleLogText(line, entryIndex, lineIndex)}
                        </div>
                      ))
                    : renderBattleLogText(action.text ?? '', entryIndex)}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      );
    }
    return renderBattleLogText(entry.text ?? '', entryIndex);
  };

  const renderCogEditor = (cog, maxHp, currentHp) => (
    <>
      <div className="battle-setup__control">
        <select
          value={cog.value}
          onChange={(event) => handleCogChange(cog.id, event.target.value)}
          className="battle-setup__select"
        >
          {COG_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {cog.value && (
        <div className="battle-setup__level">
          <label className="battle-setup__label" htmlFor={`level-${cog.id}`}>
            Level
          </label>
          <select
            id={`level-${cog.id}`}
            value={cog.level}
            onChange={(event) => handleLevelChange(cog.id, event.target.value)}
            className="battle-setup__select"
          >
            {getLevelOptions(cog.value).map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="battle-setup__hp">
        <label className="battle-setup__label" htmlFor={`hp-${cog.id}`}>
          Current HP
        </label>
        <input
          id={`hp-${cog.id}`}
          className="battle-setup__slider"
          type="range"
          min={1}
          max={maxHp || 1}
          value={currentHp || 1}
          onChange={(event) => handleHpChange(cog.id, event.target.value)}
        />
        <div className="battle-setup__hp-readout">
          {currentHp || 1}/{maxHp || 1}
        </div>
      </div>
      <div className="battle-setup__field">
        <label className="battle-setup__checkbox">
          <input
            type="checkbox"
            checked={Boolean(cog.skelecog)}
            onChange={(event) => handleSkelecogChange(cog.id, event.target.checked)}
          />
          <span>Skelecog?</span>
        </label>
      </div>
      <div className="battle-setup__field">
        <label className="battle-setup__checkbox">
          <input
            type="checkbox"
            checked={Boolean(cog.v2)}
            onChange={(event) => handleV2Change(cog.id, event.target.checked)}
          />
          <span>v2.0?</span>
        </label>
      </div>
      <div className="battle-setup__field">
        <span className="battle-setup__label">Status</span>
        <div className="status-row">
          <button
            type="button"
            className={`status-button${
              cog.status === 'trapped' ? ' status-button--active' : ''
            }`}
            onClick={() => handleCogStatusToggle(cog.id, 'trapped')}
            aria-pressed={cog.status === 'trapped'}
          >
            <img src={trappedIcon} alt="Trapped" />
          </button>
          <button
            type="button"
            className={`status-button${
              cog.status === 'lured' ? ' status-button--active' : ''
            }`}
            onClick={() => handleCogStatusToggle(cog.id, 'lured')}
            aria-pressed={cog.status === 'lured'}
          >
            <img src={luredIcon} alt="Lured" />
          </button>
        </div>
        {cog.status === 'trapped' && (
          <div className="battle-setup__field">
            <label className="battle-setup__label" htmlFor={`trap-damage-${cog.id}`}>
              Trapped for how much damage?
            </label>
            <input
              id={`trap-damage-${cog.id}`}
              className="battle-setup__input"
              type="number"
              min={0}
              value={cog.trapDamage}
              onChange={(event) => handleTrapDamageChange(cog.id, event.target.value)}
            />
          </div>
        )}
        {cog.status === 'lured' && (
          <div className="battle-setup__field">
            <label className="battle-setup__label" htmlFor={`lure-rounds-${cog.id}`}>
              Lured for how many rounds?
            </label>
            <input
              id={`lure-rounds-${cog.id}`}
              className="battle-setup__input"
              type="number"
              min={0}
              value={cog.lureRounds}
              onChange={(event) => handleLureRoundsChange(cog.id, event.target.value)}
            />
          </div>
        )}
      </div>
    </>
  );

  const openCogEditor = (cogId) => {
    setEditCogId(cogId);
    setIsCogEditOpen(true);
  };

  const closeCogEditor = () => {
    setIsCogEditOpen(false);
    setEditCogId(null);
  };

  const openToonEditor = (toonId) => {
    setEditToonId(toonId);
    setIsToonEditOpen(true);
  };

  const closeToonEditor = () => {
    setIsToonEditOpen(false);
    setEditToonId(null);
  };

  const renderGagSetupSummary = (toon) => {
    if (!Array.isArray(toon.gagSetup)) return null;
    if (!toon.gagSetup.some((entry) => entry?.maxLevel)) return null;
    const organicSet = new Set(toon.organicGags ?? []);
    return (
      <div className="gag-setup-summary" aria-label="Gag setup summary">
        {GAG_TRACKS.map((track, trackIndex) => {
          const entry = toon.gagSetup?.[trackIndex];
          const maxLevel = entry?.maxLevel ?? 0;
          const hasTrack = maxLevel > 0;
          const gagInfo = hasTrack
            ? track.gags?.[maxLevel - 1]
            : track.gags?.[0];
          const isFullyOrganic =
            maxLevel === 7 &&
            Array.from({ length: 7 }).every((_, levelIndex) =>
              organicSet.has(`${trackIndex}-${levelIndex}`)
            );
          return (
            <div
              key={`${toon.id}-track-${trackIndex}`}
              className={`gag-setup-summary__slot${
                gagInfo ? '' : ' gag-setup-summary__slot--empty'
              }${!hasTrack ? ' gag-setup-summary__slot--inactive' : ''}${
                isFullyOrganic ? ' gag-setup-summary__slot--organic' : ''
              }`}
              aria-label={
                gagInfo
                  ? `${track.name}: ${gagInfo.name}`
                  : `${track.name}: not available`
              }
            >
              {gagInfo && (
                <img
                  src={getGagImageSrc(gagInfo.file)}
                  alt={gagInfo.name}
                  className="gag-setup-summary__image"
                />
              )}
              {isFullyOrganic && (
                <img
                  src={organicTag}
                  alt="Organic"
                  className="gag-setup-summary__organic"
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderToonEditor = (toon, currentLaff) => (
    <>
      <label className="battle-setup__label">Name</label>
      <input
        className="battle-setup__input"
        type="text"
        value={toon.name}
        onChange={(event) => handleToonNameChange(toon.id, event.target.value)}
        placeholder="Enter toon name"
      />
      <div className="battle-setup__field">
        <label className="battle-setup__label" htmlFor={`max-laff-${toon.id}`}>
          Max laff
        </label>
        <input
          id={`max-laff-${toon.id}`}
          className="battle-setup__slider"
          type="range"
          min={15}
          max={140}
          value={toon.maxLaff}
          onChange={(event) => handleMaxLaffChange(toon.id, event.target.value)}
        />
        <div className="battle-setup__hp-readout">{toon.maxLaff}</div>
      </div>
      <div className="battle-setup__field">
        <label
          className="battle-setup__label"
          htmlFor={`current-laff-${toon.id}`}
        >
          Current laff
        </label>
        <input
          id={`current-laff-${toon.id}`}
          className="battle-setup__slider"
          type="range"
          min={1}
          max={toon.maxLaff}
          value={currentLaff}
          onChange={(event) =>
            handleCurrentLaffChange(toon.id, event.target.value)
          }
        />
        <div className="battle-setup__hp-readout">
          {currentLaff}/{toon.maxLaff}
        </div>
      </div>
      <div className="battle-setup__field">
        <span className="battle-setup__label">Gag</span>
        <button
          className="gag-button battle-setup__setup-gags"
          type="button"
          onClick={() => {
            closeToonEditor();
            openSetupGagsModal(toon.id, { returnToEditor: true });
          }}
        >
          Setup gags
        </button>
        {renderGagSetupSummary(toon)}
      </div>
    </>
  );

  const renderBattleLogContent = () => (
    <>
      <div className="battle-log__header">
        <h2>Log</h2>
        <div className="battle-log__controls">
          <label className="battle-log__select">
            <select
              aria-label="Select battle log turn"
              value={logViewIndex ?? ''}
              onChange={(event) => {
                const value = event.target.value;
                setLogViewIndex(value === '' ? null : Number(value));
              }}
              disabled={!battleLogHistory.length}
            >
              <option value="" disabled>
                Select turn
              </option>
              {battleLogHistory.map((entry, index) => (
                <option key={entry.label} value={index}>
                  {entry.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="battle-log__current"
            onClick={() => setLogViewIndex(null)}
            disabled={!isViewingHistory}
          >
            Current
          </button>
          <button
            type="button"
            className="battle-log__current"
            onClick={handleClearLogHistory}
            disabled={!battleLogHistory.length}
          >
            Clear
          </button>
        </div>
      </div>
      <div className="battle-log__body" ref={logBodyRef}>
        {displayedLog.length ? (
          <ol className="battle-log__list">
            {displayedLog.map((entry, index) => (
              <li key={`log-${index}`}>
                {renderBattleLogEntry(entry, index)}
              </li>
            ))}
          </ol>
        ) : (
          <p className="battle-log__empty">
            Run a turn to populate the log.
          </p>
        )}
      </div>
    </>
  );

  return (
    <div
      className={`App${
        isMobileLayout && isBattleScreen ? ' App--mobile-battle' : ''
      }`}
    >
      <div className="battle-layout">
        {isMobileLayout && (
          <div className="battle-mobile-frame">
            <div
              className="battle-mobile-shell"
              role="toolbar"
              aria-label="Quick navigation"
            >
              <div className="battle-mobile-bar battle-mobile-bar--title">
                <div className="battle-mobile-bar__title">Cog Battle Simulator</div>
              </div>
              <div className="battle-mobile-bar battle-mobile-bar--actions">
                <div className="battle-mobile-bar__actions">
                  <button
                    type="button"
                    className="battle-mobile-bar__button"
                    onClick={() => {
                      setIsTabsModalOpen(true);
                      setIsModesModalOpen(false);
                    }}
                  >
                    Tabs
                  </button>
                  <button
                    type="button"
                    className="battle-mobile-bar__button"
                    onClick={() => {
                      setIsModesModalOpen(true);
                      setIsTabsModalOpen(false);
                    }}
                    disabled={turnInProgress}
                  >
                    Modes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <main className="battle-setup">
          <header className="battle-setup__header">
            <div className="battle-setup__header-top">
              <div className="battle-setup__header-block battle-setup__header-block--brand">
                <div className="battle-setup__brand">
                  <p className="battle-setup__eyebrow">Toontown Rewritten</p>
                  <h1 className="battle-setup__title">Cog Battle Simulator</h1>
                </div>
              </div>
              <div className="battle-setup__header-block battle-setup__header-block--tabs">
                <div className="battle-setup__header-group">
                  <span className="battle-setup__header-label">Tabs</span>
                  <div
                    className="battle-tabs"
                    role="radiogroup"
                    aria-label="Page tabs"
                  >
                    <label className="battle-tabs__option">
                      <input
                        type="radio"
                        name="battle-screen"
                        value="battle"
                        checked={isBattleScreen}
                        onChange={() => handleScreenChange('battle')}
                      />
                      <span>Battle</span>
                    </label>
                    <label className="battle-tabs__option">
                      <input
                        type="radio"
                        name="battle-screen"
                        value="cogs"
                        checked={isCogsScreen}
                        onChange={() => handleScreenChange('cogs')}
                        disabled={turnInProgress}
                      />
                      <span>Cogs</span>
                    </label>
                    <label className="battle-tabs__option">
                      <input
                        type="radio"
                        name="battle-screen"
                        value="toons"
                        checked={isToonsScreen}
                        onChange={() => handleScreenChange('toons')}
                        disabled={turnInProgress}
                      />
                      <span>Toons</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="battle-setup__header-block battle-setup__header-block--modes">
                <div className="battle-setup__header-group">
                  <span className="battle-setup__header-label">Modes</span>
                  <div
                    className="battle-modes"
                    role="radiogroup"
                    aria-label="Cog lineup mode"
                  >
                    <label className="battle-modes__option">
                      <input
                        type="radio"
                        name="cog-lineup-mode"
                        value="battle"
                        checked={cogLineupMode === 'battle'}
                        onChange={() => handleCogModeChange('battle')}
                        disabled={turnInProgress}
                      />
                      <span>Battle</span>
                    </label>
                    <label className="battle-modes__option">
                      <input
                        type="radio"
                        name="cog-lineup-mode"
                        value="custom"
                        checked={cogLineupMode === 'custom'}
                        onChange={() => handleCogModeChange('custom')}
                        disabled={turnInProgress}
                      />
                    <span>Manual</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="battle-setup__header-spacer" aria-hidden="true" />
          </header>

          {isCogsScreen && (
            <section
              className={`battle-setup__panel battle-setup__panel--cogs cog-font${
                isBattleCogMode ? ' battle-setup__panel--cogs-battle' : ' battle-setup__panel--cogs-custom'
              }`}
              aria-label="Cog selection"
            >
            <div className="battle-setup__panel-header">
              <div>
                <h2>Cog lineup</h2>
                <p>
                  {isBattleCogMode
                    ? 'Cogs are generated automatically.'
                    : 'Pick up to four cogs for the battle.'}
                </p>
              </div>
              {isBattleCogMode ? (
                <div className="battle-setup__pool-controls">
                  <label className="battle-setup__pool">
                    <span className="battle-setup__pool-label">
                      Cogs remaining
                    </span>
                    <input
                      className="battle-setup__pool-slider"
                      type="range"
                      min="0"
                      max="99"
                      value={battleCogPoolSize}
                      onChange={(event) =>
                        handleBattlePoolSizeChange(event.target.value)
                      }
                      disabled={turnInProgress || battleCogEndless}
                      aria-label="Remaining cogs"
                    />
                    <span
                      className={`battle-setup__pool-value${
                        battleCogEndless
                          ? ' battle-setup__pool-value--disabled'
                          : ''
                      }`}
                    >
                      {battleCogPoolSize}
                    </span>
                  </label>
                  <label className="battle-setup__checkbox battle-setup__checkbox--inline">
                    <input
                      type="checkbox"
                      checked={battleCogEndless}
                      onChange={(event) =>
                        handleBattleEndlessChange(event.target.checked)
                      }
                      disabled={turnInProgress}
                    />
                    <span>Endless?</span>
                  </label>
                </div>
              ) : (
                <button
                  className="icon-button icon-button--add battle-setup__add"
                  type="button"
                  onClick={handleAddCog}
                  disabled={cogs.length >= 4}
                  aria-label="Add another cog"
                >
                  +
                </button>
              )}
            </div>

            {isBattleCogMode ? (
              <div className="battle-setup__config-block">
                <p className="battle-setup__label">Presets</p>
                <div className="battle-setup__radio-group" role="radiogroup">
                  <label className="battle-setup__radio">
                    <input
                      type="radio"
                      name="cog-battle-type"
                      value="cfo"
                      checked={cogBattleType === 'cfo'}
                      onChange={() => handleCogBattleTypeChange('cfo')}
                      disabled={turnInProgress}
                    />
                    <span>CFO cogs</span>
                  </label>
                  <label className="battle-setup__radio">
                    <input
                      type="radio"
                      name="cog-battle-type"
                      value="cj"
                      checked={cogBattleType === 'cj'}
                      onChange={() => handleCogBattleTypeChange('cj')}
                      disabled={turnInProgress}
                    />
                    <span>CJ cogs</span>
                  </label>
                  <label className="battle-setup__radio">
                    <input
                      type="radio"
                      name="cog-battle-type"
                      value="ceo"
                      checked={cogBattleType === 'ceo'}
                      onChange={() => handleCogBattleTypeChange('ceo')}
                      disabled={turnInProgress}
                    />
                    <span>CEO cogs</span>
                  </label>
                </div>
              </div>
            ) : (
            <div className="battle-setup__list">
              {cogs.map((cog, index) => {
                const maxHp = getMaxHp(cog.level);
                const currentHp = Math.min(cog.currentHp ?? maxHp, maxHp);
                const label = getCogLabel(cog.value);
                return (
                  <div key={cog.id} className="battle-setup__row battle-setup__row--cog">
                    <button
                      className="icon-button icon-button--copy battle-setup__copy"
                      type="button"
                      onClick={() => handleCopyCog(cog.id)}
                      disabled={cogs.length >= 4}
                      aria-label={`Copy cog ${index + 1}`}
                    >
                      ⧉
                    </button>
                    <button
                      className="icon-button icon-button--remove battle-setup__remove"
                      type="button"
                      onClick={() => handleRemoveCog(cog.id)}
                      disabled={cogs.length <= 1}
                      aria-label={`Remove cog ${index + 1}`}
                    >
                      -
                    </button>
                <h3 className="battle-setup__card-title">
                  Cog #{index + 1}
                </h3>
                  <div className="battle-setup__control">
                    <select
                      value={cog.value}
                      onChange={(event) =>
                        handleCogChange(cog.id, event.target.value)
                      }
                      className="battle-setup__select"
                    >
                      {COG_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {cog.value && (
                    <div className="battle-setup__level">
                      <label
                        className="battle-setup__label"
                        htmlFor={`level-${cog.id}`}
                      >
                      Level
                      </label>
                    <select
                      id={`level-${cog.id}`}
                      value={cog.level}
                      onChange={(event) =>
                        handleLevelChange(cog.id, event.target.value)
                      }
                      className="battle-setup__select"
                    >
                      {getLevelOptions(cog.value).map((level) => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                      </select>
                    </div>
                  )}
                  <div className="battle-setup__hp">
                    <label
                      className="battle-setup__label"
                      htmlFor={`hp-${cog.id}`}
                    >
                      Current HP
                    </label>
                    <input
                      id={`hp-${cog.id}`}
                      className="battle-setup__slider"
                      type="range"
                      min={1}
                      max={maxHp || 1}
                      value={currentHp || 1}
                      onChange={(event) =>
                        handleHpChange(cog.id, event.target.value)
                      }
                    />
                    <div className="battle-setup__hp-readout">
                      {currentHp || 1}/{maxHp || 1}
                    </div>
                  </div>
                  <div className="battle-setup__field">
                    <label className="battle-setup__checkbox">
                      <input
                        type="checkbox"
                        checked={Boolean(cog.skelecog)}
                        onChange={(event) =>
                          handleSkelecogChange(cog.id, event.target.checked)
                        }
                      />
                      <span>Skelecog?</span>
                    </label>
                  </div>
                  <div className="battle-setup__field">
                    <label className="battle-setup__checkbox">
                      <input
                        type="checkbox"
                        checked={Boolean(cog.v2)}
                        onChange={(event) =>
                          handleV2Change(cog.id, event.target.checked)
                        }
                      />
                      <span>v2.0?</span>
                    </label>
                  </div>
                  <div className="battle-setup__field">
                    <span className="battle-setup__label">Status</span>
                    <div className="status-row">
                      <button
                        type="button"
                        className={`status-button${
                          cog.status === 'trapped' ? ' status-button--active' : ''
                        }`}
                        onClick={() => handleCogStatusToggle(cog.id, 'trapped')}
                        aria-pressed={cog.status === 'trapped'}
                      >
                        <img
                          src={trappedIcon}
                          alt="Trapped"
                        />
                      </button>
                      <button
                        type="button"
                        className={`status-button${
                          cog.status === 'lured' ? ' status-button--active' : ''
                        }`}
                        onClick={() => handleCogStatusToggle(cog.id, 'lured')}
                        aria-pressed={cog.status === 'lured'}
                      >
                        <img
                          src={luredIcon}
                          alt="Lured"
                        />
                      </button>
                    </div>
                    {cog.status === 'trapped' && (
                      <div className="battle-setup__field">
                        <label
                          className="battle-setup__label"
                          htmlFor={`trap-damage-${cog.id}`}
                        >
                          Trapped for how much damage?
                        </label>
                        <input
                          id={`trap-damage-${cog.id}`}
                          className="battle-setup__input"
                          type="number"
                          min={0}
                          value={cog.trapDamage}
                          onChange={(event) =>
                            handleTrapDamageChange(cog.id, event.target.value)
                          }
                        />
                      </div>
                    )}
                    {cog.status === 'lured' && (
                      <div className="battle-setup__field">
                        <label
                          className="battle-setup__label"
                          htmlFor={`lure-rounds-${cog.id}`}
                        >
                          Lured for how many rounds?
                        </label>
                        <input
                          id={`lure-rounds-${cog.id}`}
                          className="battle-setup__input"
                          type="number"
                          min={0}
                          value={cog.lureRounds}
                          onChange={(event) =>
                            handleLureRoundsChange(cog.id, event.target.value)
                          }
                        />
                      </div>
                    )}
                  </div>
                  <div className="battle-setup__summary">
                    <span className="battle-setup__summary-line">
                      {label || 'Select cog'}
                    </span>
                    <div className="battle-setup__summary-group">
                      <span className="battle-setup__summary-line">
                        Level {cog.level || '-'}
                      </span>
                      {(cog.skelecog || cog.v2) && (
                        <span className="battle-setup__summary-line">
                          {cog.skelecog ? 'Skelecog' : 'v2.0'}
                        </span>
                      )}
                    </div>
                    <div className="battle-setup__summary-group">
                      <div className="battle-setup__summary-hp-bar">
                        <div
                          className="battle-setup__summary-hp-fill"
                          style={{
                            width: `${maxHp ? (currentHp / maxHp) * 100 : 0}%`,
                          }}
                        />
                      </div>
                      <span className="battle-setup__summary-line">
                        {currentHp || 1}/{maxHp || 1}
                      </span>
                    </div>
                    <span className="battle-setup__summary-line battle-setup__summary-status">
                      {cog.status === 'trapped' && (
                        <>
                          <img src={trappedIcon} alt="Trapped" />
                          {cog.trapDamage !== '' ? ` (${cog.trapDamage})` : ''}
                        </>
                      )}
                      {cog.status === 'lured' && (
                        <>
                          <img src={luredIcon} alt="Lured" />
                          {cog.lureRounds !== '' ? ` (${cog.lureRounds})` : ''}
                        </>
                      )}
                      {!cog.status && 'No status'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="gag-button battle-setup__edit"
                    onClick={() => openCogEditor(cog.id)}
                  >
                    Edit
                  </button>
                </div>
              );
            })}
          </div>
            )}
        </section>
          )}

        {isToonsScreen && (
        <section className="battle-setup__panel battle-setup__panel--toons" aria-label="Toon selection">
          <div className="battle-setup__panel-header">
            <div>
              <h2>Toon lineup</h2>
              <p>Pick up to four toons for the battle.</p>
            </div>
            <div className="battle-setup__panel-actions">
              {SHOW_SQUAD_SETUP_BUTTON && (
                <button
                  className="gag-button battle-setup__control"
                  type="button"
                  onClick={handleSquadSetup}
                >
                  Squad
                </button>
              )}
              <button
                className="icon-button icon-button--add battle-setup__add"
                type="button"
                onClick={handleAddToon}
                disabled={toons.length >= 4}
                aria-label="Add another toon"
              >
                +
              </button>
            </div>
          </div>

          <div className="battle-setup__list">
            {toons.map((toon, index) => {
              const currentLaff = Math.min(toon.currentLaff, toon.maxLaff);
              const hasGagSetup =
                Array.isArray(toon.gagSetup) &&
                toon.gagSetup.some((entry) => entry?.maxLevel);
              return (
                <div key={toon.id} className="battle-setup__row battle-setup__row--toon">
                  <button
                    className="icon-button icon-button--copy battle-setup__copy"
                    type="button"
                    onClick={() => handleCopyToon(toon.id)}
                    disabled={toons.length >= 4}
                    aria-label={`Copy toon ${index + 1}`}
                  >
                    ⧉
                  </button>
                  <button
                    className="icon-button icon-button--remove battle-setup__remove"
                    type="button"
                    onClick={() => handleRemoveToon(toon.id)}
                    disabled={toons.length <= 1}
                    aria-label={`Remove toon ${index + 1}`}
                  >
                    -
                  </button>
                  <h3 className="battle-setup__card-title">
                    Toon #{index + 1}
                  </h3>
                  {renderToonEditor(toon, currentLaff)}
                  <div className="battle-setup__summary">
                    <span className="battle-setup__summary-line battle-setup__summary-line--title">
                      {toon.name}
                    </span>
                    <div className="battle-setup__summary-group">
                      <div className="battle-setup__summary-hp-bar">
                        <div
                          className="battle-setup__summary-hp-fill"
                          style={{
                            width: `${
                              toon.maxLaff ? (currentLaff / toon.maxLaff) * 100 : 0
                            }%`,
                          }}
                        />
                      </div>
                      <span className="battle-setup__summary-line">
                        {currentLaff}/{toon.maxLaff}
                      </span>
                    </div>
                    <span className="battle-setup__summary-line battle-setup__summary-line--label">
                      Gags
                    </span>
                    <div className="gag-setup-summary gag-setup-summary--stacked" aria-label="Gag setup summary">
                      {(() => {
                        const organicSet = new Set(toon.organicGags ?? []);
                        return GAG_TRACKS.map((track, trackIndex) => {
                          const entry = toon.gagSetup?.[trackIndex];
                          const maxLevel = entry?.maxLevel ?? 0;
                          const hasTrack = maxLevel > 0;
                          const gagInfo = hasTrack
                            ? track.gags?.[maxLevel - 1]
                            : track.gags?.[0];
                          const isFullyOrganic =
                            maxLevel === 7 &&
                            Array.from({ length: 7 }).every((_, levelIndex) =>
                              organicSet.has(`${trackIndex}-${levelIndex}`)
                            );
                          return (
                            <div
                              key={`${toon.id}-track-summary-${trackIndex}`}
                              className={`gag-setup-summary__slot${
                                gagInfo ? '' : ' gag-setup-summary__slot--empty'
                              }${!hasTrack ? ' gag-setup-summary__slot--inactive' : ''}${
                                isFullyOrganic ? ' gag-setup-summary__slot--organic' : ''
                              }`}
                              aria-label={
                                gagInfo
                                  ? `${track.name}: ${gagInfo.name}`
                                  : `${track.name}: not available`
                              }
                            >
                              {gagInfo && (
                                <img
                                  src={getGagImageSrc(gagInfo.file)}
                                  alt={gagInfo.name}
                                  className="gag-setup-summary__image"
                                />
                              )}
                              {isFullyOrganic && (
                                <img
                                  src={organicTag}
                                  alt="Organic"
                                  className="gag-setup-summary__organic"
                                />
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="gag-button battle-setup__edit"
                    onClick={() => openToonEditor(toon.id)}
                  >
                    Edit
                  </button>
                </div>
              );
            })}
          </div>
        </section>
        )}

        {isCogsScreen && (
        <section className="battle-setup__panel cog-font" aria-label="Cog AI configuration">
          <div className="battle-setup__panel-header">
            <div>
              <h2>Cog AI config</h2>
              <p>Control how cogs choose and land attacks.</p>
            </div>
          </div>

          <div className="battle-setup__config">
            <div className="battle-setup__config-block">
              <p className="battle-setup__label">Accuracy config</p>
              <div className="battle-setup__radio-group" role="radiogroup">
                {COG_ACCURACY_OPTIONS.map((option) => (
                  <label key={option.id} className="battle-setup__radio">
                    <input
                      type="radio"
                      name="cog-accuracy-config"
                      value={option.id}
                      checked={cogAccuracyConfig === option.id}
                      onChange={() => setCogAccuracyConfig(option.id)}
                    />
                    <span>{option.label}</span>
                    <span className="battle-setup__radio-meta">
                      {option.chance}%
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="battle-setup__config-block">
              <p className="battle-setup__label">Usage config</p>
              <div className="battle-setup__radio-group" role="radiogroup">
                {COG_USAGE_OPTIONS.map((option) => (
                  <div key={option.id} className="battle-setup__radio-row">
                    <label className="battle-setup__radio">
                      <input
                        type="radio"
                        name="cog-usage-config"
                        value={option.id}
                        checked={cogUsageConfig === option.id}
                        onChange={() => setCogUsageConfig(option.id)}
                      />
                      <span>{option.label}</span>
                    </label>
                    {option.id === 'random' && (
                      <button
                        type="button"
                        className="battle-setup__reroll"
                        onClick={() => setCogRandomSeed((seed) => seed + 1)}
                        disabled={cogUsageConfig !== 'random'}
                        style={{ display: SHOW_ATTACK_PREVIEW ? 'inline-flex' : 'none' }}
                      >
                        Reroll
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="battle-setup__config-block">
              <p className="battle-setup__label">Targeting config</p>
              <div className="battle-setup__radio-group" role="radiogroup">
                {COG_TARGET_OPTIONS.map((option) => (
                  <div key={option.id} className="battle-setup__radio-row">
                    <label className="battle-setup__radio">
                      <input
                        type="radio"
                        name="cog-target-config"
                        value={option.id}
                        checked={cogTargetConfig === option.id}
                        onChange={() => setCogTargetConfig(option.id)}
                      />
                      <span>{option.label}</span>
                    </label>
                    {option.id === 'random' && (
                      <button
                        type="button"
                        className="battle-setup__reroll"
                        onClick={() =>
                          setCogTargetRandomSeed((seed) => seed + 1)
                        }
                        disabled={cogTargetConfig !== 'random'}
                        style={{ display: SHOW_ATTACK_PREVIEW ? 'inline-flex' : 'none' }}
                      >
                        Reroll
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        )}

        {isBattleScreen && (
        <section
          ref={battleDisplayRef}
          className="battle-setup__panel battle-setup__panel--battle"
          aria-label="Cog display"
          style={{ '--battle-card-scale': `${battleCardScale}` }}
        >
          <div className="battle-setup__panel-header">
            <div className="battle-setup__title-row">
              <h2>Battle</h2>
              {isBattleCogMode && (
                <p className="battle-setup__subtitle">
                  {battleCogEndless ? (
                    <>
                      <span className="battle-setup__subtitle-em">Endless</span>{' '}
                      cogs remaining
                    </>
                  ) : (
                    `${battleCogPoolRemaining} cogs remaining`
                  )}
                </p>
              )}
              {SHOW_TOON_DEFEAT_TIPS && (
                <p className="battle-setup__warning" role="status">
                  Can any Toon(s) be defeated this turn?:{' '}
                  <span
                    className={`battle-setup__warning-value${
                      defeatTipActive
                        ? ' battle-setup__warning-value--yes'
                        : ' battle-setup__warning-value--no'
                    }`}
                  >
                    {defeatTipActive ? 'Yes' : 'No'}
                  </span>
                </p>
              )}
            </div>
            <div className="battle-setup__actions">
              {lastTurnSnapshot && (
                <button
                  type="button"
                  className="gag-button battle-setup__control battle-setup__step"
                  onClick={handleUndoTurn}
                >
                  {'<<<'}
                </button>
              )}
              {!turnInProgress && (
                <button
                  type="button"
                  className="gag-button battle-setup__start"
                  onClick={handleStartTurn}
                  disabled={!canStartTurn}
                >
                  Start turn
                </button>
              )}
              {turnInProgress && (
                <>
                  <button
                    type="button"
                    className="gag-button battle-setup__control battle-setup__step"
                    onClick={handleStepBack}
                    disabled={!canStepBack}
                    aria-label="Previous move"
                  >
                    {'<'}
                  </button>
                  {isTurnReadyToEnd ? (
                    <button
                      type="button"
                      className="gag-button battle-setup__start"
                      onClick={handleEndTurn}
                    >
                      End turn
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className={`gag-button battle-setup__control battle-setup__step${
                          isMoveResolving ? ' battle-setup__control--active' : ''
                        }`}
                        onClick={handleManualStep}
                        disabled={!canAdvanceStep}
                        aria-label="Next action"
                      >
                        {isMoveResolving ? '>>' : '>'}
                      </button>
                      <button
                        type="button"
                        className="gag-button battle-setup__control battle-setup__step"
                        onClick={handleFinishTurn}
                        disabled={!canFastForward}
                        aria-label="Finish turn"
                      >
                        {'>>>'}
                      </button>
                    </>
                  )}
                </>
              )}
              <label className="battle-setup__auto">
                <span>Auto</span>
                <input
                  type="checkbox"
                  checked={autoAdvance}
                  onChange={(event) => setAutoAdvance(event.target.checked)}
                />
                <span className="battle-setup__auto-slider" aria-hidden="true" />
              </label>
            </div>
          </div>

          <div className="battle-setup__list battle-setup__list--display battle-setup__list--cogs">
            {cogs.map((cog) => {
              const toast = actionToasts.find(
                (item) => item.targetType === 'cog' && item.targetId === cog.id
              );
              const label = getCogLabel(cog.value);
              const maxHp = getMaxHp(cog.level);
              const currentHp = Math.min(cog.currentHp ?? maxHp, maxHp);
              const hpPercent = maxHp ? (currentHp / maxHp) * 100 : 0;
              const attacks = getCogAttacks(cog.value, cog.level);
              const selectedAttack = pickCogAttack(
                attacks,
                toons,
                cogUsageConfig,
                cogUsageConfig === 'random' ? cogRandomSeed : null,
                cogUsageConfig === 'random' ? cog.id : null
              );
              const selectedAttackData = selectedAttack
                ? attacks?.[selectedAttack]
                : null;
              const preview = getAttackPreview(selectedAttackData, toons);
              const isMultiAttack = selectedAttackData?.affects === 'multi';
              const targetToonId = !isMultiAttack
                ? pickCogTarget(
                    toons,
                    cogTargetConfig,
                    cogTargetRandomSeed,
                    `${cog.id}-target`
                  )
                : null;
              return (
                <div
                  key={cog.id}
                  className="battle-setup__row battle-setup__row--display battle-setup__row--cog"
                >
                  {toast && (
                    <div
                      key={toast.id}
                      className={`battle-setup__toast battle-setup__toast--${toast.tone ?? 'damage'}${
                        toast.icon ? ' battle-setup__toast--icon' : ''
                      }`}
                      aria-hidden="true"
                    >
                      {toast.icon ? (
                        <span className="battle-setup__toast-icon-wrap">
                          <img
                            src={toast.icon}
                            alt=""
                            className="battle-setup__toast-icon"
                          />
                          {toast.text && (
                            <span className="battle-setup__toast-icon-text">
                              {toast.text}
                            </span>
                          )}
                        </span>
                      ) : (
                        toast.text
                      )}
                    </div>
                  )}
                  <div className="cog-card cog-card--cog cog-font">
                    <img
                      src={getCogImageSrc(cog.value, { skelecog: cog.skelecog })}
                      alt={`${label} cog`}
                      className="cog-card__image"
                      loading="lazy"
                    />
                    <div className="cog-card__details">
                      <h3 className="cog-card__name">{label}</h3>
                  <p className="cog-card__level">
                        Level {cog.level || '-'}{cog.v2 ? ' v2.0' : ''}
                      </p>
                      <div className="cog-card__hp">
                        <div className="cog-card__hp-bar">
                          <div
                            className="cog-card__hp-fill"
                            style={{ width: `${hpPercent}%` }}
                          />
                        </div>
                        <span className="cog-card__hp-text">
                          {currentHp}/{maxHp}
                        </span>
                      </div>
                      {cog.status && (
                        <div className="cog-card__status">
                          {cog.status === 'trapped' && (
                            <div className="status-indicator status-indicator--active">
                              <img
                                src={trappedIcon}
                                alt="Trapped"
                              />
                            </div>
                          )}
                          {cog.status === 'lured' && (
                            <div className="status-indicator status-indicator--active">
                              <img
                                src={luredIcon}
                                alt="Lured"
                              />
                            </div>
                          )}
                          {cog.status === 'trapped' && cog.trapDamage !== '' && (
                            <span className="cog-card__status-text">
                              {cog.trapDamage} damage
                            </span>
                          )}
                          {cog.status === 'lured' && cog.lureRounds !== '' && (
                            <span className="cog-card__status-text">
                              {cog.lureRounds} rounds
                            </span>
                          )}
                        </div>
                      )}
                      {SHOW_ATTACK_PREVIEW && selectedAttack && (
                        <div className="cog-card__preview">
                          <p className="cog-card__preview-title">Attack preview</p>
                          <p className="cog-card__preview-row">
                            {humanizeCamelCase(selectedAttack)} · {preview.total}
                            {preview.breakdown ? ` (${preview.breakdown})` : ''}
                            {' '}total
                          </p>
                          {!isMultiAttack && toons.length > 0 && (
                            <div className="cog-card__preview-target">
                              <p className="cog-card__preview-title">Target Toon</p>
                              <div className="cog-card__target-row">
                                {toons.map((toon) => (
                                  <span key={toon.id} className="cog-card__target-mark">
                                    {toon.id === targetToonId ? 'X' : '-'}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="battle-setup__list battle-setup__list--display battle-setup__list--toons">
            {toons.map((toon) => {
              const toast = actionToasts.find(
                (item) => item.targetType === 'toon' && item.targetId === toon.id
              );
              const currentLaff = Math.min(toon.currentLaff, toon.maxLaff);
              const laffPercent = toon.maxLaff
                ? (currentLaff / toon.maxLaff) * 100
                : 0;
              const gagInfo = getGagInfo(toon.gag);
              const isToonUp = gagInfo?.trackName === 'Toon-Up';
              const isAllCogs = gagInfo?.targeting === 'all-cogs';
              const isTrap = gagInfo?.trackName === 'Trap';
              const isTrapAllCogs = isTrap && gagInfo?.targeting === 'all-cogs';
              const hasTrappedCogs =
                isTrapAllCogs && cogs.some((cog) => cog.status === 'trapped');
              const hasAllLuredCogs =
                isTrapAllCogs &&
                cogs.length > 0 &&
                cogs.every((cog) => cog.status === 'lured');
              const isLure = gagInfo?.trackName === 'Lure';
              const isLureAllCogs = isLure && gagInfo?.targeting === 'all-cogs';
              const showResolvedMark =
                turnInProgress && (toon.turnResolved || isTurnReadyToEnd);

              return (
                <div
                  key={toon.id}
                  className="battle-setup__row battle-setup__row--display battle-setup__row--toon"
                >
                  {toast && (
                    <div
                      key={toast.id}
                      className={`battle-setup__toast battle-setup__toast--${toast.tone ?? 'damage'}`}
                      aria-hidden="true"
                    >
                      {toast.text}
                    </div>
                  )}
                  <div className="cog-card cog-card--toon">
                    <div className="cog-card__details">
                      <div className="cog-card__header">
                        <h3 className="cog-card__name">{toon.name}</h3>
                      </div>
                      <button
                        type="button"
                        className="cog-card__gag-button"
                        onClick={() => openBattleGagModal(toon.id)}
                        disabled={turnInProgress}
                        aria-label={`Select gag for ${toon.name}`}
                      >
                        {showResolvedMark ? (
                          <span className="cog-card__gag-button-mark">-</span>
                        ) : gagInfo ? (
                          <img
                            src={getGagImageSrc(gagInfo.file)}
                            alt={gagInfo.gagName}
                            className="cog-card__gag-button-image"
                          />
                        ) : (
                          <span className="cog-card__gag-button-mark">
                            {toon.passSelected ? 'Pass' : '?'}
                          </span>
                        )}
                      </button>
                      {toon.gag && (
                        <div className="cog-card__target">
                          <p className="cog-card__target-label">
                            {isToonUp
                              ? gagInfo?.targeting === 'all-toons-except-self'
                                ? 'Target Toons:'
                                : 'Target Toon:'
                              : isAllCogs
                                ? 'Target Cogs:'
                                : 'Target Cog:'}
                          </p>
                          <div className="cog-card__target-row">
                            {isToonUp
                              ? toons.map((targetToon) => {
                                  const isSelf = targetToon.id === toon.id;
                                  const isSelected =
                                    toon.target?.type === 'toon' &&
                                    toon.target?.id === targetToon.id;
                                  const isAutoTarget =
                                    toon.target?.type === 'all-toons-except-self';
                                  const isLocked = isAutoTarget && !isSelf;
                                  return (
                                    <button
                                      key={targetToon.id}
                                      type="button"
                                      className={`target-button${
                                        isSelected || isLocked
                                          ? ' target-button--selected'
                                          : ''
                                      }${isSelf ? ' target-button--disabled' : ''}${
                                        isLocked ? ' target-button--locked' : ''
                                      }`}
                                      onClick={() =>
                                        handleTargetSelect(toon.id, {
                                          type: 'toon',
                                          id: targetToon.id,
                                        })
                                      }
                                      aria-pressed={isSelected || isLocked}
                                      aria-label="Target Toon"
                                      disabled={turnInProgress || isSelf || isAutoTarget}
                                    >
                                      {isSelf
                                        ? '-'
                                        : isSelected || isLocked
                                          ? '✓'
                                          : 'X'}
                                    </button>
                                  );
                                })
                              : cogs.map((cog) => {
                                  const isSelected =
                                    toon.target?.type === 'cog' &&
                                    toon.target?.id === cog.id;
                                  const isAutoTarget =
                                    toon.target?.type === 'all-cogs';
                                  const isLureEligible = cog.status !== 'lured';
                                  const isSelectable =
                                    !isTrap && !isLure
                                      ? true
                                      : isTrap
                                        ? (isTrapAllCogs && !hasTrappedCogs && !hasAllLuredCogs) ||
                                          !cog.status
                                        : isLure
                                          ? isLureEligible
                                          : true;
                                  const isTargeted = isAutoTarget
                                    ? isLureAllCogs
                                      ? isLureEligible
                                      : true
                                    : isSelected;
                                  return (
                                    <button
                                      key={cog.id}
                                      type="button"
                                      className={`target-button${
                                        isTargeted
                                          ? ' target-button--selected'
                                          : ''
                                      }${isAutoTarget ? ' target-button--locked' : ''}${
                                        !isSelectable ? ' target-button--disabled' : ''
                                      }`}
                                      onClick={() =>
                                        handleTargetSelect(toon.id, {
                                          type: 'cog',
                                          id: cog.id,
                                        })
                                      }
                                      aria-pressed={isSelected}
                                      aria-label="Target Cog"
                                      disabled={
                                        turnInProgress || isAutoTarget || !isSelectable
                                      }
                                    >
                                      {!isSelectable
                                        ? '-'
                                        : isTargeted
                                          ? '✓'
                                          : 'X'}
                                    </button>
                                  );
                                })}
                          </div>
                        </div>
                      )}
                      <div className="cog-card__hp">
                        <div className="cog-card__hp-bar">
                          <div
                            className="cog-card__hp-fill"
                            style={{ width: `${laffPercent}%` }}
                          />
                        </div>
                        <span className="cog-card__hp-text">
                          {currentLaff}/{toon.maxLaff}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
        )}
        {isBattleScreen && isMobileLayout && (
          <section className="battle-log battle-log--inline" aria-label="Battle log">
            {renderBattleLogContent()}
          </section>
        )}
        </main>
        {isBattleScreen && !isMobileLayout && (
          <aside className="battle-log" aria-label="Battle log">
            {renderBattleLogContent()}
          </aside>
        )}
      </div>
      {isTabsModalOpen && isMobileLayout && (
        <div className="ui-modal" role="dialog" aria-modal="true">
          <div
            className="ui-modal__backdrop"
            onClick={() => setIsTabsModalOpen(false)}
          />
          <div className="ui-modal__content">
            <div className="ui-modal__header">
              <h3>Tabs</h3>
              <button
                type="button"
                className="ui-modal__close"
                onClick={() => setIsTabsModalOpen(false)}
                aria-label="Close tabs"
              >
                X
              </button>
            </div>
            <div className="ui-modal__body">
              <div className="battle-tabs" role="radiogroup" aria-label="Page tabs">
                <label className="battle-tabs__option">
                  <input
                    type="radio"
                    name="battle-screen-mobile"
                    value="battle"
                    checked={isBattleScreen}
                    onChange={() => {
                      handleScreenChange('battle');
                      setIsTabsModalOpen(false);
                    }}
                  />
                  <span>Battle</span>
                </label>
                <label className="battle-tabs__option">
                  <input
                    type="radio"
                    name="battle-screen-mobile"
                    value="cogs"
                    checked={isCogsScreen}
                    onChange={() => {
                      handleScreenChange('cogs');
                      setIsTabsModalOpen(false);
                    }}
                    disabled={turnInProgress}
                  />
                  <span>Cogs</span>
                </label>
                <label className="battle-tabs__option">
                  <input
                    type="radio"
                    name="battle-screen-mobile"
                    value="toons"
                    checked={isToonsScreen}
                    onChange={() => {
                      handleScreenChange('toons');
                      setIsTabsModalOpen(false);
                    }}
                    disabled={turnInProgress}
                  />
                  <span>Toons</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
      {isModesModalOpen && isMobileLayout && (
        <div className="ui-modal" role="dialog" aria-modal="true">
          <div
            className="ui-modal__backdrop"
            onClick={() => setIsModesModalOpen(false)}
          />
          <div className="ui-modal__content">
            <div className="ui-modal__header">
              <h3>Modes</h3>
              <button
                type="button"
                className="ui-modal__close"
                onClick={() => setIsModesModalOpen(false)}
                aria-label="Close modes"
              >
                X
              </button>
            </div>
            <div className="ui-modal__body">
              <div className="battle-modes" role="radiogroup" aria-label="Cog lineup mode">
                <label className="battle-modes__option">
                  <input
                    type="radio"
                    name="cog-lineup-mode-mobile"
                    value="battle"
                    checked={cogLineupMode === 'battle'}
                    onChange={() => {
                      handleCogModeChange('battle');
                      setIsModesModalOpen(false);
                    }}
                    disabled={turnInProgress}
                  />
                  <span>Battle</span>
                </label>
                <label className="battle-modes__option">
                  <input
                    type="radio"
                    name="cog-lineup-mode-mobile"
                    value="custom"
                    checked={cogLineupMode === 'custom'}
                    onChange={() => {
                      handleCogModeChange('custom');
                      setIsModesModalOpen(false);
                    }}
                    disabled={turnInProgress}
                  />
                  <span>Manual</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
      {isCogEditOpen && isMobileLayout && (
        <div className="ui-modal" role="dialog" aria-modal="true">
          <div className="ui-modal__backdrop" onClick={closeCogEditor} />
          {(() => {
            const editingIndex = cogs.findIndex((cog) => cog.id === editCogId);
            const editingCog = cogs[editingIndex];
            if (!editingCog) return null;
            const maxHp = getMaxHp(editingCog.level);
            const currentHp = Math.min(editingCog.currentHp ?? maxHp, maxHp);
            return (
              <div className="ui-modal__content">
                <div className="ui-modal__header">
                  <h3>Edit Cog #{editingIndex + 1}</h3>
                  <button
                    type="button"
                    className="ui-modal__close"
                    onClick={closeCogEditor}
                    aria-label="Close cog editor"
                  >
                    X
                  </button>
                </div>
                <div className="ui-modal__body ui-modal__body--cog">
                  {renderCogEditor(editingCog, maxHp, currentHp)}
                </div>
              </div>
            );
          })()}
        </div>
      )}
      {isToonEditOpen && isMobileLayout && (
        <div className="ui-modal" role="dialog" aria-modal="true">
          <div className="ui-modal__backdrop" onClick={closeToonEditor} />
          {(() => {
            const editingIndex = toons.findIndex((toon) => toon.id === editToonId);
            const editingToon = toons[editingIndex];
            if (!editingToon) return null;
            const currentLaff = Math.min(editingToon.currentLaff, editingToon.maxLaff);
            return (
              <div className="ui-modal__content">
                <div className="ui-modal__header">
                  <h3>Edit Toon #{editingIndex + 1}</h3>
                  <button
                    type="button"
                    className="ui-modal__close"
                    onClick={closeToonEditor}
                    aria-label="Close toon editor"
                  >
                    X
                  </button>
                </div>
                <div className="ui-modal__body ui-modal__body--cog">
                  {renderToonEditor(editingToon, currentLaff)}
                </div>
              </div>
            );
          })()}
        </div>
      )}
      {isBattleGagModalOpen && (
        <div className="gag-modal" role="dialog" aria-modal="true">
          <div className="gag-modal__backdrop" onClick={closeBattleGagModal} />
          <div className="gag-modal__content">
            {(() => {
              const battleToon = toons.find((toon) => toon.id === battleGagToonId);
              const activeGag = battleToon?.gag;
              const organicSet = new Set(battleToon?.organicGags ?? []);
              const gagSetup = battleToon?.gagSetup ?? [];
              return (
                <>
                  <div className="gag-modal__header">
                    <div>
                      <h3>Select a gag</h3>
                    </div>
                    <div className="gag-modal__header-actions">
                      <button
                        type="button"
                        className="gag-modal__skip"
                        onClick={handleBattlePass}
                      >
                        Pass
                      </button>
                      <button
                        className="gag-modal__close"
                        type="button"
                        onClick={closeBattleGagModal}
                        aria-label="Close gag selection"
                      >
                        X
                      </button>
                    </div>
                  </div>
                  <div className="gag-modal__image">
                    <div className="gag-modal__grid">
                      {Array.from({ length: 7 }).map((_, row) =>
                        Array.from({ length: 7 }).map((__, col) => {
                          const track = GAG_TRACKS[row];
                          const gagInfo = track?.gags?.[col];
                          const maxLevel = gagSetup?.[row]?.maxLevel ?? 0;
                          const isOwned = maxLevel > 0 && col + 1 <= maxLevel;
                          const organicKey = getOrganicKey(row, col);
                          const isOrganic = organicSet.has(organicKey);
                          const hasTrapTargets = cogs.some((cog) => !cog.status);
                          const hasLureTargets = cogs.some((cog) => cog.status !== 'lured');
                          const anyTrapped = cogs.some((cog) => cog.status === 'trapped');
                          const allLured =
                            cogs.length > 0 && cogs.every((cog) => cog.status === 'lured');
                          const isRailroad = row === 1 && gagInfo?.name === 'Railroad';
                          const isTrapDisabled =
                            row === 1 &&
                            (isRailroad ? anyTrapped || allLured : !hasTrapTargets);
                          const isLureDisabled = row === 2 && !hasLureTargets;
                          const isDisabled =
                            !isOwned ||
                            (row === 0 && toons.length < 2) ||
                            isTrapDisabled ||
                            isLureDisabled;
                          const isActive =
                            activeGag?.track === row + 1 &&
                            activeGag?.level === col + 1;
                          const trackColor = track?.name
                            ? GAG_TRACK_COLORS[track.name]
                            : null;
                          const trackRgb =
                            trackColor != null ? hexToRgb(trackColor) : null;
                          return (
                            <button
                              key={`${row}-${col}`}
                              type="button"
                              className={`gag-modal__cell${
                                isActive ? ' gag-modal__cell--active' : ''
                              }${isDisabled ? ' gag-modal__cell--disabled' : ''}${
                                isOrganic ? ' gag-modal__cell--organic' : ''
                              }`}
                              onClick={() => handleBattleGagSelect(row, col)}
                              disabled={isDisabled}
                              style={
                                trackRgb
                                  ? { '--gag-track-color-rgb': trackRgb.join(', ') }
                                  : null
                              }
                              aria-label={
                                gagInfo
                                  ? `${track.name} - ${gagInfo.name} (Level ${
                                      col + 1
                                    })`
                                  : 'Empty gag slot'
                              }
                            >
                              {gagInfo && isOwned && (
                                <img
                                  src={getGagImageSrc(gagInfo.file)}
                                  alt={gagInfo.name}
                                  className="gag-modal__gag-icon"
                                />
                              )}
                              {isOrganic && (
                                <img
                                  className="gag-modal__organic-icon"
                                  src={organicTag}
                                  alt=""
                                  aria-hidden="true"
                                />
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
      {isSetupGagsOpen && (
        <div className="gag-modal" role="dialog" aria-modal="true">
          <div className="gag-modal__backdrop" onClick={closeSetupGagsModal} />
          <div className="gag-modal__content">
            {(() => {
              const isOrganicSetup = setupTrackIndex >= GAG_TRACKS.length;
              const setupToon = toons.find((toon) => toon.id === setupToonId);
              const organicSet = new Set(setupToon?.organicGags ?? []);
              const currentTrack = GAG_TRACKS[setupTrackIndex];
              return (
                <>
            <div className="gag-modal__header">
              <div>
                <h3>
                  {isOrganicSetup
                    ? 'Organic gags?'
                    : `Strongest ${currentTrack?.name ?? ''} gag?`}
                </h3>
              </div>
              <div className="gag-modal__header-actions">
                {!isOrganicSetup && (
                  <button
                    type="button"
                    className="gag-modal__skip"
                    onClick={handleSetupNoTrack}
                  >
                    I don't have {currentTrack?.name}
                  </button>
                )}
                <button
                  className="gag-modal__close"
                  type="button"
                  onClick={closeSetupGagsModal}
                  aria-label="Close gag setup"
                >
                  X
                </button>
              </div>
            </div>
            <div className="gag-modal__image">
              <div className="gag-modal__grid">
                {Array.from({ length: 7 }).map((_, row) =>
                  Array.from({ length: 7 }).map((__, col) => {
                    const track = GAG_TRACKS[row];
                    const gagInfo = track?.gags?.[col];
                    const maxLevel = setupToon?.gagSetup?.[row]?.maxLevel ?? 0;
                    const isOwned = maxLevel && col + 1 <= maxLevel;
                    const organicKey = getOrganicKey(row, col);
                    const isSelected = organicSet.has(organicKey);
                    const missingOrganicCount =
                      isOrganicSetup && !isSelected
                        ? Array.from({ length: col + 1 }).filter(
                            (_, idx) =>
                              !organicSet.has(getOrganicKey(row, idx))
                          ).length
                        : 0;
                    const wouldExceedLimit =
                      isOrganicSetup &&
                      !isSelected &&
                      organicSet.size + missingOrganicCount > 8;
                    const isDisabled = isOrganicSetup
                      ? !isOwned || wouldExceedLimit
                      : row !== setupTrackIndex;
                    const isActive = isOrganicSetup
                      ? isSelected
                      : row === setupTrackIndex && maxLevel === col + 1;
                    const trackColor = track?.name
                      ? GAG_TRACK_COLORS[track.name]
                      : null;
                    const trackRgb =
                      trackColor != null ? hexToRgb(trackColor) : null;
                    return (
                      <button
                        key={`${row}-${col}`}
                        type="button"
                        className={`gag-modal__cell${
                          isActive ? ' gag-modal__cell--active' : ''
                        }${isDisabled ? ' gag-modal__cell--disabled' : ''}`}
                        onClick={() =>
                          isOrganicSetup
                            ? handleSetupOrganicToggle(row, col)
                            : handleSetupGagSelect(row, col)
                        }
                        disabled={isDisabled}
                        style={
                          trackRgb
                            ? { '--gag-track-color-rgb': trackRgb.join(', ') }
                            : null
                        }
                        aria-label={
                          gagInfo
                            ? `${track.name} - ${gagInfo.name} (Level ${
                                col + 1
                              })`
                            : 'Empty gag slot'
                        }
                      >
                        {gagInfo && (
                          <img
                            src={getGagImageSrc(gagInfo.file)}
                            alt={gagInfo.name}
                            className="gag-modal__gag-icon"
                          />
                        )}
                        {isOrganicSetup && isSelected && (
                          <img
                            className="gag-modal__organic-icon"
                            src={organicTag}
                            alt=""
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
            {isOrganicSetup && setupToon && (
              <div className="gag-modal__power">
                {GAG_TRACKS.map((track, trackIndex) => {
                  const entry = setupToon.gagSetup?.[trackIndex];
                  if (!entry || !entry.maxLevel) return null;
                  const gagInfo = track?.gags?.[entry.maxLevel - 1];
                  if (!gagInfo || track.name === 'Lure') return null;
                  const min = gagInfo.min ?? null;
                  const max = gagInfo.max ?? null;
                  if (min == null || max == null || min === max) return null;
                  const organicKey = getOrganicKey(
                    trackIndex,
                    entry.maxLevel - 1
                  );
                  const isOrganic = organicSet.has(organicKey);
                  const rangeMin =
                    isOrganic && gagInfo.organicMin != null
                      ? gagInfo.organicMin
                      : min;
                  const rangeMax =
                    isOrganic && gagInfo.organicMax != null
                      ? gagInfo.organicMax
                      : max;
                  const currentValueRaw = entry.maxPower ?? rangeMax;
                  const currentValue = Math.min(
                    Math.max(currentValueRaw, rangeMin),
                    rangeMax
                  );
                  const label = gagInfo.kind === 'heal' ? 'Laff' : 'Damage';
                  return (
                    <div key={`${track.name}-${gagInfo.name}`}>
                      <label className="battle-setup__label">
                        {track.name} - {gagInfo.name} {label}
                      </label>
                      <input
                        className="battle-setup__slider"
                        type="range"
                        min={rangeMin}
                        max={rangeMax}
                        value={currentValue}
                        onChange={(event) =>
                          updateToonGagSetup(setupToon.id, trackIndex, {
                            maxPower: Number.parseInt(event.target.value, 10),
                          })
                        }
                      />
                      <div className="gag-modal__power-readout">
                        {currentValue}
                      </div>
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="gag-modal__done"
                  onClick={handleSetupOrganicDone}
                >
                  Done
                </button>
              </div>
            )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;




